/**
 * Cliente HTTP genérico para a API do Sentinela.
 *
 * Dois modos:
 *  - admin: cookie do Cloudflare Access acompanha automaticamente,
 *    JWT interno emitido pela rota /api/admin/sessao/iniciar é guardado
 *    em sessionStorage e enviado como Bearer.
 *  - cliente: JWT cliente emitido após OTP, guardado em localStorage,
 *    enviado como Bearer nas rotas /api/publico/notificacoes*.
 */

// Mesmo origin do frontend: a API responde via Worker route
// `sentinela.usetelecom.com.br/api/*`. Cookie CF Access viaja automaticamente.
const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

const STORAGE_TOKEN_ADMIN = "sentinela:token:admin";
const STORAGE_TOKEN_CLIENTE = "sentinela:token:cliente";
const STORAGE_USER_ADMIN = "sentinela:user:admin";

function lerTokenAdmin(): string | null {
  return sessionStorage.getItem(STORAGE_TOKEN_ADMIN);
}
export function definirTokenAdmin(token: string | null): void {
  if (token) sessionStorage.setItem(STORAGE_TOKEN_ADMIN, token);
  else sessionStorage.removeItem(STORAGE_TOKEN_ADMIN);
}

export interface UsuarioLogado {
  id: string;
  email: string;
  nome: string;
  role: string;
}

/**
 * Lê o usuário admin logado do sessionStorage. Disponível depois do
 * SessionGate executar `iniciarSessaoAdmin` com sucesso. Usado pra:
 *  - mostrar/esconder UI baseada em role (ex: dashboard de auditoria
 *    só pra master)
 *  - exibir nome/email do operador na sidebar / forms
 *
 * Retorna null se ninguém logado. Frontend NÃO toma decisões de
 * segurança baseado nisso — backend re-checa com requerPermissao().
 */
export function lerUsuarioAdmin(): UsuarioLogado | null {
  const raw = sessionStorage.getItem(STORAGE_USER_ADMIN);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UsuarioLogado;
  } catch {
    return null;
  }
}

export function definirUsuarioAdmin(user: UsuarioLogado | null): void {
  if (user) sessionStorage.setItem(STORAGE_USER_ADMIN, JSON.stringify(user));
  else sessionStorage.removeItem(STORAGE_USER_ADMIN);
}

/** Identidade + origem de quem GEROU um documento (export forense). O IP
 *  vem do servidor (CF-Connecting-IP) — autoridade real, não o navegador. */
export interface GeradorExport {
  email: string;
  nome?: string;
  role?: string;
  ip?: string | null;
  user_agent?: string | null;
  agora_utc?: string;
}

let _geradorCache: GeradorExport | null = null;

/**
 * Resolve quem está gerando o export (para carimbar PDF/CSV forense). Faz
 * GET /sessao/whoami (IP confiável do servidor) e funde o nome do usuário
 * logado. Cacheado por sessão de página. Em falha (offline/sem rede) cai
 * pro que tem no sessionStorage — o documento ainda sai identificado pelo
 * usuário, só sem IP.
 */
export async function obterGeradorExport(): Promise<GeradorExport> {
  if (_geradorCache) return _geradorCache;
  const local = lerUsuarioAdmin();
  try {
    const r = await apiFetch<{
      ok: true;
      email: string;
      role: string;
      ip: string | null;
      user_agent: string | null;
      agora_utc: string;
    }>("/api/admin/sessao/whoami", { ehAdmin: true });
    _geradorCache = {
      email: r.email,
      nome: local?.nome,
      role: r.role,
      ip: r.ip,
      user_agent: r.user_agent,
      agora_utc: r.agora_utc,
    };
  } catch {
    _geradorCache = {
      email: local?.email ?? "—",
      nome: local?.nome,
      role: local?.role,
      ip: null,
    };
  }
  return _geradorCache;
}

function lerTokenCliente(): string | null {
  return localStorage.getItem(STORAGE_TOKEN_CLIENTE);
}
export function definirTokenCliente(token: string | null): void {
  if (token) localStorage.setItem(STORAGE_TOKEN_CLIENTE, token);
  else localStorage.removeItem(STORAGE_TOKEN_CLIENTE);
}

interface OpcoesFetch extends RequestInit {
  ehAdmin?: boolean;
  ehCliente?: boolean;
}

export async function apiFetch<T = unknown>(
  caminho: string,
  opcoes: OpcoesFetch = {},
): Promise<T> {
  const { ehAdmin, ehCliente, headers, ...rest } = opcoes;
  // Quando body é FormData, NÃO setar Content-Type — o browser monta o
  // boundary multipart automaticamente. Forçar application/json quebra
  // o parser do backend (form.entries() vira vazio).
  const ehMultipart =
    typeof FormData !== "undefined" && rest.body instanceof FormData;
  const finalHeaders: Record<string, string> = {
    ...(ehMultipart ? {} : { "content-type": "application/json" }),
    ...((headers as Record<string, string> | undefined) ?? {}),
  };
  if (ehAdmin) {
    const t = lerTokenAdmin();
    if (t) finalHeaders.authorization = `Bearer ${t}`;
  }
  if (ehCliente) {
    const t = lerTokenCliente();
    if (t) finalHeaders.authorization = `Bearer ${t}`;
  }

  const res = await fetch(`${BASE}${caminho}`, {
    ...rest,
    headers: finalHeaders,
    credentials: ehAdmin ? "include" : "omit",
  });
  const dados = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    erro?: string;
    [k: string]: unknown;
  };
  if (!res.ok || dados.ok === false) {
    throw new ErroApi(dados.erro ?? `http_${res.status}`, res.status, dados);
  }
  return dados as T;
}

export class ErroApi extends Error {
  constructor(
    public codigo: string,
    public status: number,
    public corpo: unknown,
  ) {
    // Se o backend retornou { detalhe: "..." } OU { detalhes: "..." } (ex:
    // rate-limit-ator usa plural), incluir esse texto na message. Ajuda
    // muito a diagnosticar "rate_limit_ator" e "erro_interno" genéricos.
    const c = corpo as { detalhe?: unknown; detalhes?: unknown } | null;
    const detalhe =
      c && typeof c === "object"
        ? typeof c.detalhe === "string"
          ? c.detalhe
          : typeof c.detalhes === "string"
            ? c.detalhes
            : ""
        : "";
    super(detalhe ? `${codigo}: ${detalhe}` : codigo);
    this.name = "ErroApi";
  }
}

// ===== Endpoints públicos (cliente) =====

export interface RespostaSolicitarAcesso {
  ok: true;
  mensagem: string;
}
export function solicitarAcessoCliente(email: string, cartaId?: string) {
  return apiFetch<RespostaSolicitarAcesso>("/api/publico/acesso/solicitar", {
    method: "POST",
    body: JSON.stringify({ email, ...(cartaId ? { carta_id: cartaId } : {}) }),
  });
}

export interface RespostaVerificarOtp {
  ok: true;
  token: string;
  expires_in: number;
}
export function verificarOtp(email: string, codigo: string) {
  return apiFetch<RespostaVerificarOtp>("/api/publico/acesso/verificar", {
    method: "POST",
    body: JSON.stringify({ email, codigo }),
  });
}

export interface NotificacaoCliente {
  id: string;
  assunto: string;
  status: string;
  enviada_em: string | null;
  criado_em: string;
  /** Tipo do alerta originador (tftp_aberto, ddos_origem, etc). null se órfão. */
  tipo_alerta: string | null;
  /** baixa | media | alta | critica */
  severidade: string | null;
  incidente_id: string | null;
  /** ID humano formato SNT-YYYYMMDD-NNNN — pra referência em ticket/atendimento */
  incidente_id_humano: string | null;
  incidente_status: string | null;
  incidente_resolvido_em: string | null;
}
export interface ResumoCliente {
  total: number;
  abertos: number;
  resolvidos: number;
  ultimos_30d: number;
  por_severidade: { baixa: number; media: number; alta: number; critica: number };
}
export interface RespostaListaNotificacoes {
  ok: true;
  notificacoes: NotificacaoCliente[];
  resumo: ResumoCliente;
}
export function listarNotificacoesCliente() {
  return apiFetch<RespostaListaNotificacoes>("/api/publico/notificacoes", {
    ehCliente: true,
  });
}

export interface DetalheNotificacao {
  ok: true;
  carta: {
    id: string;
    assunto: string;
    corpo: string;
    status: string;
    enviada_em: string | null;
  };
  alerta: {
    id: string;
    tipo_alerta: string;
    severidade: string;
    test_date: string | null;
    incidente_id: string;
  };
  /**
   * Última verificação do par (cliente, alerta). null = nunca foi verificado
   * (geralmente caso B2B onde probe foi pulado). Quando status === "resolvido"
   * + resolvido_metodo === "cliente_confirmou", a UI mostra read-only o que
   * o cliente já enviou (descrição + lista de evidências).
   */
  verificacao: {
    id: string;
    status: "pendente" | "em_verificacao" | "resolvido" | "cancelado";
    resolvido_em: string | null;
    resolvido_metodo:
      | "manual"
      | "probe_tcp"
      | "ausencia_alertas"
      | "cliente_confirmou"
      | null;
    descricao_resolucao_cliente: string | null;
    evidencias_json: string | null;
    confirmado_via_email: string | null;
  } | null;
}
export function obterNotificacaoCliente(id: string) {
  return apiFetch<DetalheNotificacao>(`/api/publico/notificacoes/${id}`, {
    ehCliente: true,
  });
}

/**
 * Cliente confirma resolução pela área pública. Aceita texto obrigatório
 * (≥10 chars) + até 3 arquivos (PNG/JPG/WEBP/PDF/TXT/LOG, máx 5MB cada).
 *
 * Idempotente — se já confirmado antes, retorna `ja_resolvido_antes: true`
 * sem reabrir verificação.
 */
export function confirmarResolucaoCliente(
  cartaId: string,
  dados: { descricao: string; evidencias: File[] },
) {
  const form = new FormData();
  form.append("descricao", dados.descricao);
  for (const f of dados.evidencias) {
    form.append("evidencia", f, f.name);
  }
  return apiFetch<{
    ok: true;
    ja_resolvido_antes: boolean;
    verificacao_id: string;
    evidencias_aceitas: number;
    falhas_upload: Array<{ nome: string; erro: string }>;
  }>(`/api/publico/notificacoes/${cartaId}/resolver`, {
    method: "POST",
    ehCliente: true,
    body: form,
  });
}

// ===== Endpoints admin =====

export interface AlertaResumido {
  id: string;
  tipo_alerta: string;
  severidade: string;
  status: string;
  asn: string;
  received_at: string;
  test_date: string | null;
  /** Primeiro IP do alerta (denormalizado pra busca/exibição) */
  ip_principal: string | null;
  /** ID do cliente identificado (se houver) */
  cliente_id: string | null;
  /** Nome do cliente identificado (se houver) */
  cliente_nome: string | null;
  /** True quando há verificacoes_incidente.status='resolvido' pra este alerta */
  resolvido: boolean;
  resolvido_em: string | null;
  resolvido_metodo: string | null;
  /**
   * Estado operacional do incidente associado. Null = pipeline normal.
   * Quando setado, a linha do alerta mostra badge correspondente.
   */
  status_followup: StatusFollowup | null;
  /** Analista atribuído ao incidente (null = sem dono). */
  responsavel_id: string | null;
  responsavel_email: string | null;
  responsavel_nome: string | null;
}
export function listarAlertasAdmin(
  opts: {
    incluirResolvidos?: boolean;
    somenteResolvidos?: boolean;
    somenteSemCliente?: boolean;
    /** Filtra apenas alertas atribuídos ao user logado (responsavel_id). */
    somenteMeus?: boolean;
    /** Alertas em quarentena (incidente.status_followup="quarentena"). */
    somenteQuarentena?: boolean;
    /** Alertas descartados (incidente.status_followup="descartado"). */
    somenteDescartados?: boolean;
    somenteIncidentesAbertos?: boolean;
    /** Filtro de período (YYYY-MM-DD) por received_at — opcional. */
    de?: string;
    ate?: string;
    /** Busca no SERVIDOR (3+ caracteres): tipo, severidade, status, ASN, IP, cliente. Conta e pagina o conjunto inteiro. */
    q?: string;
    /** Coluna e direção no SERVIDOR (lista branca na rota). */
    ordenar?: string;
    dir?: "asc" | "desc";
    pagina?: number;
    limit?: number;
  } = {},
) {
  const params: string[] = [];
  // Precedência: meus > quarentena > sem_cliente > resolvidos > incluir_resolvidos.
  if (opts.somenteIncidentesAbertos) params.push("somente_incidentes_abertos=true");
  else if (opts.somenteMeus) params.push("somente_meus=true");
  else if (opts.somenteQuarentena) params.push("somente_quarentena=true");
  else if (opts.somenteDescartados) params.push("somente_descartados=true");
  else if (opts.somenteSemCliente) params.push("somente_sem_cliente=true");
  else if (opts.somenteResolvidos) params.push("somente_resolvidos=true");
  else if (opts.incluirResolvidos) params.push("incluir_resolvidos=true");
  if (opts.de) params.push(`de=${encodeURIComponent(opts.de)}`);
  if (opts.ate) params.push(`ate=${encodeURIComponent(opts.ate)}`);
  if (opts.q) params.push(`q=${encodeURIComponent(opts.q)}`);
  if (opts.ordenar) params.push(`ordenar=${encodeURIComponent(opts.ordenar)}`);
  if (opts.dir) params.push(`dir=${opts.dir}`);
  if (opts.pagina && opts.pagina > 1) params.push(`pagina=${opts.pagina}`);
  if (opts.limit) params.push(`limit=${opts.limit}`);
  const qs = params.length ? `?${params.join("&")}` : "";
  return apiFetch<{
    ok: true;
    alertas: AlertaResumido[];
    qtd_total: number;
    qtd_resolvidos: number;
    qtd_sem_cliente: number;
    qtd_meus: number;
    qtd_quarentena: number;
    qtd_descartados: number;
    qtd_incidentes_abertos?: number;
    qtd_filtrado: number;
    pagina: number;
    limit: number;
    total_paginas: number;
  }>(`/api/admin/alertas${qs}`, {
    ehAdmin: true,
  });
}

export interface DetalheAlerta {
  ok: true;
  alerta: {
    id: string;
    tipo_alerta: string;
    severidade: string;
    status: string;
    asn: string;
    fonte: string;
    received_at: string;
    test_date: string | null;
    raw_excerpt: string | null;
    incidente_id: string;
    email_message_id: string;
    alert_hash: string;
    criado_em: string;
  };
  ips: Array<{
    id: string;
    ip: string;
    protocolo: string | null;
    porta: number | null;
    test_status: string | null;
    test_result: string | null;
    cliente_id: string | null;
    lookup_status: "pendente" | "ok" | "nao_encontrado" | "aguardando_vinculo" | "erro";
    snapshot_id: string | null;
  }>;
  snapshots: Array<{
    id: string;
    ip: string;
    payload_json: string;
    duracao_ms: number | null;
    lookup_em: string;
  }>;
  clientes: Array<{
    id: string;
    nome: string;
    contrato: string | null;
    fonte: string;
  }>;
  incidente: {
    id: string;
    /** ID humano formato "SNT-YYYYMMDD-NNNN" — usar em comunicação ao cliente/NOC */
    id_humano: string | null;
    tipo_alerta: string;
    severidade: string;
    status: string;
    ip_principal: string | null;
    qtd_alertas: number;
    qtd_reincidencias: number;
    primeira_ocorrencia_em: string;
    ultima_ocorrencia_em: string;
    /** N1=informativo, N2=atuação padrão, N3=escalado (NOC paginado) */
    nivel_procedural: "N1" | "N2" | "N3";
    nivel_procedural_atualizado_em: string | null;
    nivel_procedural_motivo: string | null;
    /** Estado operacional ortogonal ao status técnico. Null = sem follow-up especial. */
    status_followup: StatusFollowup | null;
    status_followup_desde: string | null;
    status_followup_motivo: string | null;
    /** Analista atribuído ao caso (null = sem dono). */
    responsavel_id: string | null;
    responsavel_email: string | null;
    responsavel_desde: string | null;
  } | null;
  email: {
    id: string;
    gmail_message_id: string;
    from_email: string;
    subject: string;
    received_at: string;
    parser_status: string;
  } | null;
  cartas: Array<{
    id: string;
    /** Cliente DESTINATÁRIO da carta — pode não ser mais o do alerta. */
    cliente_id: string;
    cliente_nome: string | null;
    assunto: string;
    corpo: string;
    status: string;
    modo_envio: string;
    enviada_em: string | null;
    enviada_para_json: string | null;
    aprovada_em: string | null;
    criado_em: string;
    erro_envio: string | null;
  }>;
  verificacoes: VerificacaoIncidenteApi[];
  notas: NotaIncidente[];
}

/**
 * Espelha `STATUS_FOLLOWUP` de packages/db/src/schema/alertas.ts — o web
 * redeclara em vez de importar do pacote. Ao adicionar um estado novo lá,
 * adicione AQUI também, senão o TS reclama só na hora do uso.
 */
export type StatusFollowup =
  | "aguardando_cliente"
  | "aguardando_terceiro"
  | "escalado_interno"
  | "resolvido_externo"
  | "quarentena"
  | "descartado";

export type TipoNotaIncidente =
  | "geral"
  | "contato_cliente"
  | "aguardando_resposta"
  | "escalado_interno"
  | "resolvido_externo"
  | "investigacao";

export interface NotaIncidente {
  id: string;
  incidente_id: string;
  autor_id: string | null;
  autor_email: string;
  tipo: TipoNotaIncidente;
  texto: string;
  /** 0 = interna ao NOC; 1 = exibida ao cliente no portal /cliente */
  visivel_cliente: number;
  criado_em: string;
}

export interface VerificacaoIncidenteApi {
  id: string;
  incidente_id: string;
  alerta_id: string;
  carta_id: string | null;
  cliente_id: string;
  ip: string;
  porta: number | null;
  protocolo: string | null;
  tipo_alerta: string;
  status: "pendente" | "em_verificacao" | "resolvido" | "cancelado";
  agendado_para: string;
  tentativas: number;
  ultima_verificacao_em: string | null;
  ultima_verificacao_metodo: string | null;
  ultima_verificacao_resultado_json: string | null;
  resolvido_em: string | null;
  resolvido_metodo: "manual" | "probe_tcp" | "ausencia_alertas" | "cliente_confirmou" | null;
  resolvido_por: string | null;
  carta_agradecimento_id: string | null;
  criado_em: string;
}

export function obterAlerta(id: string) {
  return apiFetch<DetalheAlerta>(`/api/admin/alertas/${id}`, { ehAdmin: true });
}

// ─── Notas e follow-up de incidentes ──────────────────────────────────
// Caso de uso: registrar contatos com cliente, escalações internas, e
// estado operacional ("aguardando cliente") sem precisar de planilha
// externa. Notas opcionalmente visíveis no portal /cliente como
// comunicação formal.

export function criarNotaIncidente(
  incidenteId: string,
  payload: {
    tipo?: TipoNotaIncidente;
    texto: string;
    visivel_cliente?: boolean;
  },
) {
  return apiFetch<{ ok: true; nota: NotaIncidente }>(
    `/api/admin/incidentes/${incidenteId}/notas`,
    {
      method: "POST",
      ehAdmin: true,
      body: JSON.stringify(payload),
    },
  );
}

export function apagarNotaIncidente(incidenteId: string, notaId: string) {
  return apiFetch<{ ok: true }>(
    `/api/admin/incidentes/${incidenteId}/notas/${notaId}`,
    { method: "DELETE", ehAdmin: true },
  );
}

export function definirStatusFollowup(
  incidenteId: string,
  payload: { status_followup: StatusFollowup | null; motivo?: string },
) {
  return apiFetch<{
    ok: true;
    status_followup: StatusFollowup | null;
    status_followup_desde: string | null;
  }>(`/api/admin/incidentes/${incidenteId}/status-followup`, {
    method: "PATCH",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

export interface IncidenteAguardandoCliente {
  id: string;
  id_humano: string | null;
  cliente_id: string | null;
  cliente_nome: string | null;
  tipo_alerta: string;
  severidade: string;
  ip_principal: string | null;
  status_followup_desde: string | null;
  status_followup_motivo: string | null;
}

export function listarAguardandoCliente() {
  return apiFetch<{ ok: true; lista: IncidenteAguardandoCliente[] }>(
    `/api/admin/incidentes/_/aguardando-cliente`,
    { ehAdmin: true },
  );
}

export interface UsuarioAtivo {
  id: string;
  nome: string;
  email: string;
  role: string;
}

export function listarUsuariosAtivos() {
  return apiFetch<{ ok: true; usuarios: UsuarioAtivo[] }>(
    `/api/admin/incidentes/_/usuarios-ativos`,
    { ehAdmin: true },
  );
}

/**
 * Atribui ou libera o responsável pelo incidente.
 *  - responsavel_id: string → atribui a esse usuário (ativo)
 *  - responsavel_id: null   → libera (próximo a interagir vira responsável)
 */
export function definirResponsavelIncidente(
  incidenteId: string,
  responsavel_id: string | null,
) {
  return apiFetch<{
    ok: true;
    responsavel_id: string | null;
    responsavel_email: string | null;
    responsavel_desde: string | null;
  }>(`/api/admin/incidentes/${incidenteId}/responsavel`, {
    method: "PATCH",
    ehAdmin: true,
    body: JSON.stringify({ responsavel_id }),
  });
}

/**
 * Reprocessa TODOS os alertas pendentes em bloco. Retorna agregado.
 */
export function reprocessarAlertasPendentes() {
  return apiFetch<{
    ok: true;
    qtd_alertas_varridos: number;
    alertas_com_match: number;
    total: ResultadoRetroatividadeApi;
  }>(`/api/admin/alertas/reprocessar-pendentes`, {
    method: "POST",
    ehAdmin: true,
  });
}

/**
 * Roda verificação manual (probe TCP + ausência de alertas) das verificações
 * ativas deste alerta. Retorna resultados — se aplicável, já marca resolvido
 * e dispara carta de agradecimento.
 */
export function verificarAlertaAgora(id: string) {
  return apiFetch<{
    ok: true;
    resultados: Array<{
      verificacao_id: string;
      resolvido?: boolean;
      metodo?: string;
      detalhes?: string;
      probe?: { aberto: boolean | null; detalhes: string; metodo: string };
      alertas_recentes?: number;
      janela_horas?: number;
      erro?: string;
    }>;
  }>(`/api/admin/alertas/${id}/verificar-agora`, {
    method: "POST",
    ehAdmin: true,
  });
}

/**
 * Força marcação como resolvido manualmente. Gera carta de agradecimento
 * que vai direto pra fila de envio (status aprovada).
 */
export function marcarAlertaResolvido(id: string, observacao?: string) {
  return apiFetch<{
    ok: true;
    verificacao_id: string;
    carta_agradecimento_id: string | null;
  }>(`/api/admin/alertas/${id}/marcar-resolvido`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ observacao }),
  });
}

/**
 * Reprocessa um alerta: tenta identificar IPs pendentes contra os vínculos
 * locais (clientes_ips + clientes_subnets) e gera carta se casar.
 * Idempotente. Útil pra rodar logo após cadastrar cliente e amarrar bloco.
 */
export function reprocessarAlerta(id: string) {
  return apiFetch<{
    ok: true;
    alerta_id: string;
    total: ResultadoRetroatividadeApi;
    ips_tentados: number;
    ips_identificados: Array<{ ip: string; cliente_id: string }>;
    ips_sem_match: string[];
  }>(`/api/admin/alertas/${id}/reprocessar`, {
    method: "POST",
    ehAdmin: true,
  });
}

/** Envia o aviso (carta/e-mail) ao cliente vinculado — gera se faltar, aprova e
 *  envia na hora. Para alertas que chegaram sem cliente e foram vinculados depois. */
export function enviarAvisoAlerta(id: string) {
  return apiFetch<{
    ok: boolean;
    gerou_carta?: boolean;
    enviadas?: number;
    falhas?: number;
    cartas?: Array<{ id: string; status: string; erro: string | null }>;
    erro?: string;
    mensagem?: string;
    detalhe?: string;
  }>(`/api/admin/alertas/${id}/enviar-aviso`, {
    method: "POST",
    ehAdmin: true,
  });
}

export interface InfoRdapApi {
  alvo: string;
  tipo: "ip" | "asn";
  consultado_em: string;
  indisponivel: boolean;
  motivo?: string;
  nome: string | null;
  faixa: string | null;
  organizacao: string | null;
  pais: string | null;
  abuse_emails: string[];
  status: string[];
  ultima_atualizacao: string | null;
  fonte_rir: string | null;
}

/**
 * Lookup RDAP de um IP externo (ou ASN). Cache 24h no backend.
 * Útil pra identificar dono de IP reportador sem sair do Sentinela.
 */
export function lookupRdap(opts: { ip?: string; asn?: number }) {
  const qs = new URLSearchParams();
  if (opts.ip) qs.set("ip", opts.ip);
  if (opts.asn) qs.set("asn", String(opts.asn));
  return apiFetch<{ ok: true; info: InfoRdapApi }>(
    `/api/admin/alertas/_/rdap?${qs.toString()}`,
    { ehAdmin: true },
  );
}

// ============================================================================
// DDoS Inbound (Arbor Sightline)
// ============================================================================

export type CenarioDdosInbound =
  | "backbone_use"
  | "cliente_b2b"
  | "cliente_residencial"
  | "nao_identificado";

export type StatusDdosInbound = "em_andamento" | "encerrado" | "arquivado";

export interface IncidenteDdosInboundItem {
  id: string;
  arbor_alert_id: string;
  cliente_id: string | null;
  ip_destino: string;
  ip_versao: number;
  cenario: CenarioDdosInbound;
  managed_object: string | null;
  signatures: string[];
  importance: "Low" | "Medium" | "High";
  url_arbor: string | null;
  impact_mbps_inicio: number | null;
  impact_kpps_inicio: number | null;
  impact_mbps_fim: number | null;
  impact_kpps_fim: number | null;
  impact_mbps_pico: number | null;
  impact_kpps_pico: number | null;
  /**
   * Pico RELIDO do e-mail do Arbor. O campo `impact_mbps_pico` acima está nulo
   * em ~45% das linhas porque o parser só aceita `Mbps` e o Arbor manda os
   * ataques grandes em `Gbps` — ver `apps/api/src/lib/ddos-analise.ts`. Quem
   * exibe volume usa ESTE campo.
   */
  impact_mbps_pico_medido: number | null;
  impact_kpps_pico_medido: number | null;
  pico_recuperado_do_email: boolean;
  start_time: string;
  stop_time: string | null;
  duracao_segundos: number | null;
  status: StatusDdosInbound;
  carta_aviso_id: string | null;
  carta_fechamento_id: string | null;
  email_start_id: string | null;
  email_done_id: string | null;
  noc_avisado_em: string | null;
  criado_em: string;
  atualizado_em: string;
  cliente: { id: string; nome: string; eh_provedor_b2b: boolean } | null;
}

export interface FiltrosDdosInbound {
  status?: StatusDdosInbound;
  cenario?: CenarioDdosInbound;
  cliente_id?: string;
  /** Busca ampla: IP, id do alerta, assinatura, objeto gerenciado, cenário e nome do cliente. */
  q?: string;
  /** Dia da Bahia (AAAA-MM-DD) do INÍCIO do ataque, inclusivos. */
  de?: string;
  desde?: string;
  ate?: string;
  /** Não existe ordem por pico: o pico mostrado é o relido do e-mail, não a coluna (ver a rota). */
  ordenar?: "start_time" | "status" | "ip_destino" | "cenario" | "importance" | "duracao_segundos" | "cliente_nome";
  dir?: "asc" | "desc";
  limit?: number;
  pagina?: number;
}

export function listarDdosInbound(filtros: FiltrosDdosInbound = {}) {
  const qs = new URLSearchParams();
  if (filtros.status) qs.set("status", filtros.status);
  if (filtros.cenario) qs.set("cenario", filtros.cenario);
  if (filtros.cliente_id) qs.set("cliente_id", filtros.cliente_id);
  if (filtros.q) qs.set("q", filtros.q);
  if (filtros.de) qs.set("de", filtros.de);
  if (filtros.desde) qs.set("desde", filtros.desde);
  if (filtros.ate) qs.set("ate", filtros.ate);
  if (filtros.ordenar) qs.set("ordenar", filtros.ordenar);
  if (filtros.dir) qs.set("dir", filtros.dir);
  if (filtros.limit) qs.set("limit", String(filtros.limit));
  if (filtros.pagina && filtros.pagina > 1) qs.set("pagina", String(filtros.pagina));
  const sufixo = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<{
    ok: true;
    total: number;
    pagina: number;
    limit: number;
    total_paginas: number;
    contagens: Record<StatusDdosInbound, number>;
    incidentes: IncidenteDdosInboundItem[];
  }>(`/api/admin/ddos-inbound${sufixo}`, { ehAdmin: true });
}

export type OrigemBlocoDdos = "nossa_rede" | "terceiro" | "indeterminado";

export interface ResumoOrigemDdos {
  incidentes: number;
  ips: number;
  horas: number;
  pico_mbps: number | null;
}

export interface TopIpDdos {
  id: string;
  ip: string;
  ip_versao: number;
  origem: OrigemBlocoDdos;
  incidentes: number;
  pico_mbps: number | null;
  pico_kpps: number | null;
  horas: number;
  primeiro: string;
  ultimo: string;
  assinaturas: string[];
  cliente_nome: string | null;
  so_limiar_banda: boolean;
}

/**
 * Uma linha do top de volumetria — **um IP, não um alerta**. `id`, `quando`,
 * `duracao_segundos` e `assinaturas` descrevem o ALERTA DO PICO, para o
 * drill-down continuar abrindo o alerta específico.
 */
export interface TopVolumeDdos {
  id: string;
  arbor_alert_id: string;
  ip: string;
  origem: OrigemBlocoDdos;
  /** Alertas deste IP no período. */
  incidentes: number;
  pico_mbps: number;
  pico_kpps: number | null;
  quando: string;
  duracao_segundos: number | null;
  assinaturas: string[];
  recuperado_do_email: boolean;
  so_limiar_banda: boolean;
}

export interface AnaliseDdosInbound {
  gerado_em: string;
  periodo: { desde: string; ate: string; dias: number };
  totais: {
    incidentes: number;
    ips_distintos: number;
    horas_sob_alerta: number;
    pico_maximo_mbps: number | null;
    pico_maximo_ip: string | null;
    pico_maximo_kpps: number | null;
    media_diaria: number;
  };
  origem: {
    nossa_rede: ResumoOrigemDdos;
    terceiro: ResumoOrigemDdos;
    indeterminado: ResumoOrigemDdos;
    prefixos_terceiro: Array<{ prefixo: string; incidentes: number; ips: number }>;
  };
  /** As TRÊS origens separadas — `indeterminado` nunca é somado a `terceiro`. */
  serie_diaria: Array<{
    dia: string;
    nossa_rede: number;
    terceiro: number;
    indeterminado: number;
    total: number;
  }>;
  serie_mensal: Array<{ mes: string; incidentes: number; ips: number; horas: number }>;
  perfil_horario: Array<{ hora: number; limiar_banda: number; vetor: number }>;
  perfil_semana: Array<{ dia: number; incidentes: number }>;
  top_frequencia: TopIpDdos[];
  top_volume: TopVolumeDdos[];
  top_volume_campo_banco: Array<{ ip: string; pico_mbps: number }>;
  top_clientes: Array<{
    cliente_id: string;
    nome: string;
    eh_provedor_b2b: boolean;
    /** A própria Use — infraestrutura nossa cadastrada como cliente, não cliente. */
    eh_infraestrutura_propria: boolean;
    incidentes: number;
    ips: number;
    pico_mbps: number | null;
  }>;
  assinaturas: Array<{
    assinatura: string;
    incidentes: number;
    ips: number;
    duracao_media_segundos: number;
    pico_maximo_mbps: number | null;
  }>;
  duracao: Array<{ faixa: string; incidentes: number }>;
  qualidade: {
    pico_nulo_no_banco: number;
    pico_recuperado_do_email: number;
    pico_indisponivel: number;
    unidades: Array<{ unidade: string; incidentes: number }>;
    sem_cliente: number;
    sem_cliente_pct: number;
    sem_email_start: number;
    stop_time_nulo: number;
    duracao_nula: number;
    cenarios: Array<{ cenario: string; incidentes: number }>;
    status: Array<{ status: string; incidentes: number }>;
    importancias: Array<{ importancia: string; incidentes: number }>;
    managed_objects: Array<{ managed_object: string; incidentes: number }>;
  };
}

/**
 * Relatório agregado do período, numa chamada só. `dias` é ignorado quando
 * `desde`/`ate` vêm preenchidos (o calendário manda no chip).
 */
export function obterAnaliseDdosInbound(filtros: {
  dias?: number;
  desde?: string;
  ate?: string;
}) {
  const qs = new URLSearchParams();
  if (filtros.desde) qs.set("desde", filtros.desde);
  if (filtros.ate) qs.set("ate", filtros.ate);
  if (filtros.dias && !filtros.desde) qs.set("dias", String(filtros.dias));
  return apiFetch<{ ok: true; analise: AnaliseDdosInbound }>(
    `/api/admin/ddos-inbound/analise?${qs.toString()}`,
    { ehAdmin: true },
  );
}

/**
 * Baixa o RELATÓRIO do período em HTML, pronto para o navegador imprimir em PDF.
 *
 * ⚠️ Não dá para simplesmente apontar um `window.open()` para a rota: toda rota
 * admin exige `Authorization: Bearer`, e uma aba nova não carrega o token do
 * `sessionStorage`. Por isso o HTML vem por `fetch` autenticado e é escrito na
 * janela que a tela abriu — a autenticação continua sendo a mesma do resto do
 * painel, sem token em query string (que iria parar no log da Cloudflare e no
 * histórico do navegador).
 *
 * Devolve texto, não JSON — daí não usar `apiFetch`, que faz `res.json()`.
 */
export async function obterRelatorioDdosInboundHtml(filtros: {
  dias?: number;
  desde?: string;
  ate?: string;
}): Promise<string> {
  const qs = new URLSearchParams();
  if (filtros.desde) qs.set("desde", filtros.desde);
  if (filtros.ate) qs.set("ate", filtros.ate);
  if (filtros.dias && !filtros.desde) qs.set("dias", String(filtros.dias));

  const cabecalhos: Record<string, string> = {};
  const token = lerTokenAdmin();
  if (token) cabecalhos.authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}/api/admin/ddos-inbound/relatorio?${qs.toString()}`, {
    headers: cabecalhos,
    credentials: "include",
  });
  if (!res.ok) {
    // A rota devolve JSON em erro e HTML em sucesso — ler como texto nos dois
    // casos e só tentar o JSON aqui evita um "Unexpected token <" opaco.
    const texto = await res.text().catch(() => "");
    let codigo = `http_${res.status}`;
    try {
      const corpo = JSON.parse(texto) as { erro?: string };
      if (corpo.erro) codigo = corpo.erro;
    } catch {
      /* resposta não-JSON: fica o http_<status> */
    }
    throw new ErroApi(codigo, res.status, texto);
  }
  return res.text();
}

export interface DetalheDdosInbound {
  ok: true;
  incidente: IncidenteDdosInboundItem;
  cliente: ClienteAdmin | null;
  cartas: Array<{
    id: string;
    assunto: string;
    corpo: string;
    status: string;
    enviada_em: string | null;
  }>;
  emails: Array<{
    id: string;
    from_email: string;
    subject: string;
    received_at: string;
  }>;
}

export function obterDdosInbound(id: string) {
  return apiFetch<DetalheDdosInbound>(`/api/admin/ddos-inbound/${id}`, {
    ehAdmin: true,
  });
}

// ─── Akvorado — contrato `akvorado-sentinela/2` (especificação 2.3) ───────
// docs/integracoes/akvorado-v2/. Rotas de leitura em routes/admin/ddos-inbound.ts.

export type AmbienteAkvorado = "producao" | "homologacao";
export type FiltroAmbienteAkvorado = AmbienteAkvorado | "todos";
export type IdentificacaoAkvorado = "pendente" | "concluida" | "falha";
export type EfeitoRetratoAkvorado = "estado_atual" | "arquivado_historico";

export interface EventoAkvoradoItem {
  ambiente: AmbienteAkvorado;
  evento_id: string;
  versao: number;
  recebido_id: string;
  escopo: "host" | "prefixo";
  destino_ip: string | null;
  destino_prefixo: string | null;
  prefixo_monitorado: string | null;
  incidente_id: string | null;
  vetor: string;
  classificacao: "observacao" | "suspeito" | "confirmado";
  severidade: "informativa" | "baixa" | "media" | "alta" | "critica";
  deteccao_estado: "ativo" | "normalizado" | "telemetria_perdida" | "encerrado";
  deteccao_inicio: string;
  deteccao_fim: string | null;
  retrato_em: string;
  metricas_estado: "medidas" | "parciais" | "indisponiveis";
  bps: number | null;
  pps: number | null;
  bps_pico: number | null;
  origens_consulta: string;
  origens_total: number | null;
  qtd_decisoes: number;
  qtd_mitigacoes: number;
  mitigacao_ativa: boolean;
  identificacao: IdentificacaoAkvorado;
  identificacao_tentativas: number;
  identificado_em: string | null;
  cliente_id: string | null;
  cenario: CenarioDdosInbound | null;
  qtd_clientes: number | null;
  primeiro_recebimento_em: string;
  atualizado_em: string;
  cliente_nome?: string | null;
}

export interface FiltrosEventosAkvorado {
  ambiente?: FiltroAmbienteAkvorado;
  q?: string;
  classificacao?: string;
  severidade?: string;
  situacao?: "ativo" | "encerrado";
  vetor?: string;
  escopo?: "host" | "prefixo";
  identificacao?: IdentificacaoAkvorado;
  mitigacao?: "ativa";
  medicao?: "sem";
  desde?: string;
  ate?: string;
  ordem?: "recente" | "inicio" | "volume";
  pagina?: number;
  por_pagina?: number;
}

function sufixoQuery(f: object): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function listarEventosAkvorado(f: FiltrosEventosAkvorado = {}) {
  return apiFetch<{
    ok: true;
    total: number;
    pagina: number;
    por_pagina: number;
    contagens: {
      total: number;
      ativos: number;
      confirmados: number;
      alta_critica: number;
      mitigacao_ativa: number;
      sem_medicao: number;
      identificacao_pendente: number;
      quarentena: number;
    };
    vetores: string[];
    eventos: EventoAkvoradoItem[];
  }>(`/api/admin/ddos-inbound/akvorado${sufixoQuery(f)}`, { ehAdmin: true });
}

export interface RecebimentoAkvorado {
  id: string;
  ambiente: AmbienteAkvorado;
  metodo: "POST" | "GET";
  evento_id: string | null;
  versao: number | null;
  webhook_id: string | null;
  recebido_id: string | null;
  resultado: string;
  http_status: number;
  detalhe: unknown;
  recebido_em: string;
}

export function listarRecebimentosAkvorado(opcoes: { ambiente?: FiltroAmbienteAkvorado; limit?: number; resultado?: string; metodo?: "POST" | "GET" } = {}) {
  return apiFetch<{ ok: true; recebimentos: RecebimentoAkvorado[] }>(
    `/api/admin/ddos-inbound/akvorado/recebimentos${sufixoQuery(opcoes)}`,
    { ehAdmin: true },
  );
}

export interface QuarentenaAkvorado {
  id: string;
  ambiente: AmbienteAkvorado;
  evento_id: string;
  versao: number;
  webhook_id: string;
  motivo: string;
  corpo_sha256: string;
  recebido_id_aceito: string | null;
  corpo_sha256_aceito: string | null;
  recebido_em: string;
}

export function listarQuarentenaAkvorado(ambiente: FiltroAmbienteAkvorado) {
  return apiFetch<{ ok: true; itens: QuarentenaAkvorado[] }>(`/api/admin/ddos-inbound/akvorado/quarentena${sufixoQuery({ ambiente })}`, { ehAdmin: true });
}

export interface RetratoResumoAkvorado {
  recebido_id: string;
  versao: number;
  efeito: EfeitoRetratoAkvorado | null;
  retrato_em: string;
  recebido_em: string;
  webhook_id: string;
  corpo_sha256: string;
}

export interface ClienteAfetadoAkvorado {
  cliente_id: string;
  nome: string | null;
  origem: string;
  faixa: string | null;
  eh_provedor_b2b: boolean | null;
}

export interface DetalheEventoAkvorado {
  ok: true;
  evento: EventoAkvoradoItem;
  /** Retrato vigente, como o Akvorado mandou (contrato 2). */
  documento: Record<string, any> | null; // eslint-disable-line @typescript-eslint/no-explicit-any
  clientes_afetados: ClienteAfetadoAkvorado[];
  retratos: RetratoResumoAkvorado[];
  recebimentos: RecebimentoAkvorado[];
  quarentena: QuarentenaAkvorado[];
  avisos: AvisoNocAkvorado[];
}

/** Aviso ao NOC (migration 0034). O HTML não vem: o texto é o mesmo conteúdo. */
export interface AvisoNocAkvorado {
  id: string;
  ambiente: AmbienteAkvorado;
  evento_id: string;
  motivo: "inicio" | "agravamento" | "encerramento";
  nivel: string;
  classificacao: string;
  severidade: string;
  versao: number;
  recebido_id: string;
  destinatario: string;
  estado: "pendente" | "enviando" | "enviado" | "falha";
  tentativas: number;
  assunto: string;
  texto: string;
  resend_id: string | null;
  erro: string | null;
  criado_em: string;
  atualizado_em: string;
  enviado_em: string | null;
}

export function obterEventoAkvorado(ambiente: AmbienteAkvorado, eventoId: string) {
  return apiFetch<DetalheEventoAkvorado>(
    `/api/admin/ddos-inbound/akvorado/evento/${ambiente}/${encodeURIComponent(eventoId)}`,
    { ehAdmin: true },
  );
}

export function obterRetratoAkvorado(recebidoId: string) {
  return apiFetch<{ ok: true; retrato: RetratoResumoAkvorado & { ambiente: AmbienteAkvorado; evento_id: string; documento: unknown } }>(
    `/api/admin/ddos-inbound/akvorado/retrato/${encodeURIComponent(recebidoId)}`,
    { ehAdmin: true },
  );
}

export function arquivarDdosInbound(id: string) {
  return apiFetch<{ ok: true }>(`/api/admin/ddos-inbound/${id}/arquivar`, {
    method: "POST",
    ehAdmin: true,
  });
}

// ============================================================================
// IOC — Banco de indicadores próprio
// ============================================================================

export type TipoIoc = "ip" | "cidr" | "dominio" | "hash" | "url";
export type StatusIoc = "ativo" | "expirado" | "inativo";
export type SeveridadeIoc = "info" | "baixa" | "media" | "alta" | "critica";
export type OrigemIoc = "manual" | "auto_alerta" | "externa";

export interface IocItem {
  id: string;
  tipo: TipoIoc;
  valor: string;
  valor_normalizado: string;
  severidade: SeveridadeIoc;
  origem: OrigemIoc;
  status: StatusIoc;
  descricao: string | null;
  tags_json: string | null;
  primeiro_visto_em: string | null;
  ultimo_visto_em: string | null;
  qtd_hits: number;
  expira_em: string | null;
  criado_em: string;
  atualizado_em: string;
}

export interface FiltrosIoc {
  tipo?: TipoIoc;
  status?: StatusIoc;
  severidade?: SeveridadeIoc;
  q?: string;
}

export function listarIoc(filtros: FiltrosIoc = {}) {
  const qs = new URLSearchParams();
  if (filtros.tipo) qs.set("tipo", filtros.tipo);
  if (filtros.status) qs.set("status", filtros.status);
  if (filtros.severidade) qs.set("severidade", filtros.severidade);
  if (filtros.q) qs.set("q", filtros.q);
  const sufixo = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<{ ok: true; total: number; indicadores: IocItem[] }>(
    `/api/admin/ioc${sufixo}`,
    { ehAdmin: true },
  );
}

export interface PayloadNovoIoc {
  tipo: TipoIoc;
  valor: string;
  severidade?: SeveridadeIoc;
  descricao?: string | null;
  tags?: string[];
  expira_em?: string | null;
}

export function criarIoc(dados: PayloadNovoIoc) {
  return apiFetch<{ ok: true; id: string }>("/api/admin/ioc", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(dados),
  });
}

export function inativarIoc(id: string) {
  return apiFetch<{ ok: true }>(`/api/admin/ioc/${id}`, {
    method: "DELETE",
    ehAdmin: true,
  });
}

export function atualizarIoc(
  id: string,
  dados: Partial<{
    severidade: SeveridadeIoc;
    status: StatusIoc;
    descricao: string | null;
    tags: string[] | null;
    expira_em: string | null;
  }>,
) {
  return apiFetch<{ ok: true }>(`/api/admin/ioc/${id}`, {
    method: "PATCH",
    ehAdmin: true,
    body: JSON.stringify(dados),
  });
}

export function verificarIoc(tipo: TipoIoc, valor: string) {
  return apiFetch<{ ok: true; hit: boolean; indicador: IocItem | null }>(
    "/api/admin/ioc/verificar",
    {
      method: "POST",
      ehAdmin: true,
      body: JSON.stringify({ tipo, valor }),
    },
  );
}

/**
 * Override manual do nível procedural (N1/N2/N3) de um incidente.
 * Audita. Não dispara alerta NOC (operador já está ciente).
 */
export function definirNivelProcedural(
  incidente_id: string,
  nivel: "N1" | "N2" | "N3",
  motivo: string,
) {
  return apiFetch<{ ok: true }>(
    `/api/admin/alertas/incidentes/${incidente_id}/nivel`,
    {
      method: "POST",
      ehAdmin: true,
      body: JSON.stringify({ nivel, motivo }),
    },
  );
}

export interface CartaListItem {
  id: string;
  alerta_id: string;
  cliente_id: string;
  template_id: string;
  assunto: string;
  status: string;
  criado_em: string;
  modo_envio: string;
  aprovada_por: string | null;
  aprovada_em: string | null;
  enviada_em: string | null;
  enviada_para_json: string | null;
  tentativas_envio: number;
  erro_envio: string | null;
  cliente_nome: string | null;
}

// Backward-compat
export type CartaPendente = CartaListItem;

export function listarCartas(
  statuses: string[] = ["aguardando_aprovacao"],
  opts: {
    modo_envio?: "auto" | "manual";
    cliente_id?: string;
    /** Busca no servidor (3+ caracteres). */
    q?: string;
    ordenar?: "criado_em" | "assunto" | "cliente_nome" | "status" | "modo_envio";
    dir?: "asc" | "desc";
    pagina?: number;
    limit?: number;
    /** Período por data de ENVIO, dia da Bahia (AAAA-MM-DD). */
    de?: string;
    ate?: string;
  } = {},
) {
  const params: string[] = [`status=${statuses.join(",")}`];
  if (opts.de) params.push(`de=${opts.de}`);
  if (opts.ate) params.push(`ate=${opts.ate}`);
  if (opts.q) params.push(`q=${encodeURIComponent(opts.q)}`);
  if (opts.ordenar) params.push(`ordenar=${opts.ordenar}`);
  if (opts.dir) params.push(`dir=${opts.dir}`);
  if (opts.modo_envio) params.push(`modo_envio=${opts.modo_envio}`);
  if (opts.cliente_id) params.push(`cliente_id=${encodeURIComponent(opts.cliente_id)}`);
  if (opts.pagina && opts.pagina > 1) params.push(`pagina=${opts.pagina}`);
  if (opts.limit) params.push(`limit=${opts.limit}`);
  return apiFetch<{
    ok: true;
    cartas: CartaListItem[];
    contagens_status: Record<string, number>;
    qtd_filtrado: number;
    pagina: number;
    limit: number;
    total_paginas: number;
  }>(`/api/admin/cartas?${params.join("&")}`, {
    ehAdmin: true,
  });
}

export function listarCartasPendentes() {
  return apiFetch<{ ok: true; cartas: CartaPendente[] }>(
    "/api/admin/cartas/pendentes",
    { ehAdmin: true },
  );
}

export interface DetalheCarta {
  ok: true;
  carta: {
    id: string;
    alerta_id: string;
    cliente_id: string;
    template_id: string;
    assunto: string;
    corpo: string;
    status: string;
    modo_envio: string;
    aprovada_por: string | null;
    aprovada_em: string | null;
    enviada_em: string | null;
    enviada_para_json: string | null;
    tentativas_envio: number;
    erro_envio: string | null;
    criado_em: string;
    atualizado_em: string;
  };
  cliente: { id: string; nome: string; external_customer_id: string | null } | null;
  cliente_emails: Array<{
    id: string;
    cliente_id: string;
    tipo: "notificacao" | "tecnico" | "financeiro" | "outro";
    email: string;
    principal: boolean;
    nome_contato: string | null;
  }>;
  cliente_telefones: Array<{
    id: string;
    cliente_id: string;
    tipo: "celular" | "whatsapp" | "fixo" | "outro";
    numero: string;
    principal: boolean;
  }>;
  alerta: {
    id: string;
    tipo_alerta: string;
    severidade: string;
    asn: string;
    test_date: string | null;
    raw_excerpt: string | null;
  } | null;
  ips: Array<{
    id: string;
    alerta_id: string;
    ip: string;
    protocolo: string | null;
    porta: number | null;
    cliente_id: string | null;
    lookup_status: string;
  }>;
  snapshot: { id: string; ip: string; payload_json: string | null; duracao_ms: number } | null;
  template: { id: string; nome: string; variante: string | null } | null;
  logs_envio: Array<{
    id: string;
    acao: string;
    detalhe_json: string | null;
    usuario_id: string | null;
    criado_em: string;
  }>;
  destinatarios_planejados: Array<{
    email: string;
    tipo: string;
    principal: boolean;
    nome_contato: string | null;
  }>;
  destinatarios_efetivos: string[];
  /** CC padrão aplicado em TODAS as cartas (config mail_cc_padrao, ex.: noc@usetelecom.com.br) */
  cc_planejado: string[];
  /** CC que efetivamente foi enviado (preenchido após envio) */
  cc_efetivo: string[];
}

export function obterCarta(id: string) {
  return apiFetch<DetalheCarta>(`/api/admin/cartas/${id}`, { ehAdmin: true });
}

export function aprovarCarta(id: string, opts: { enviar_agora?: boolean } = {}) {
  return apiFetch<{
    ok: true;
    enviada?: boolean;
    ciclos?: number;
    erro_envio?: string;
    status?: string;
  }>(`/api/admin/cartas/${id}/aprovar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ enviar_agora: opts.enviar_agora ?? false }),
  });
}

/**
 * Aprova TODAS as cartas pendentes em uma chamada (máx 200 por execução).
 * Se `ids` for omitido, aprova todas. Com `enviar_agora`, dispara envio
 * imediato (drena fila até 10 ciclos).
 *
 * Rate-limit 5/h/ator — operação massiva, não deve rodar com frequência.
 */
export function aprovarCartasEmMassa(opts: { ids?: string[]; enviar_agora?: boolean } = {}) {
  return apiFetch<{
    ok: true;
    aprovadas: number;
    enviadas: number;
    falhas: number;
    detalhes?: string;
  }>("/api/admin/cartas/aprovar-em-massa", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({
      ids: opts.ids,
      enviar_agora: opts.enviar_agora ?? false,
    }),
  });
}

/**
 * Força envio imediato de uma carta já aprovada (pula a fila do cron 5min).
 */
export function enviarCartaAgora(id: string) {
  return apiFetch<{
    ok: true;
    status: "enviada";
    ciclos: number;
  }>(`/api/admin/cartas/${id}/enviar-agora`, {
    method: "POST",
    ehAdmin: true,
  });
}

export function cancelarCarta(id: string, motivo?: string) {
  return apiFetch<{ ok: true }>(`/api/admin/cartas/${id}/cancelar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ motivo }),
  });
}

export interface ClienteAdmin {
  id: string;
  nome: string;
  razao_social: string | null;
  nome_fantasia: string | null;
  /** "ixc:<id>" quando o cliente foi importado do ERP IXC. */
  external_customer_id_ixc?: string | null;
  contrato: string | null;
  documento: string | null;
  pop: string | null;
  endereco_cidade: string | null;
  endereco_uf: string | null;
  tags_json: string | null;
  politica_notificacao: "auto" | "manual" | "bloqueado";
  fonte: "sentinela" | "phpipam" | "merged";
  external_customer_id: string | null;
  phpipam_synced_at: string | null;
  /** Cliente é provedor B2B (sub-ISP) — host comprometido fica atrás de NAT.
   *  Pipeline pula probe pra DDoS origem; usa janela_ausencia_dias maior. */
  eh_provedor_b2b: boolean;
  /** Janela em dias de ausência de novos alertas pra inferir resolução.
   *  Default 3; B2B sobe pra 14+ */
  janela_ausencia_dias: number;
  /** ASN do cliente (BGP — peering/trânsito). null = não-BGP. */
  asn: number | null;
  /** VLAN default no PE (1..4094). */
  vlan_default: number | null;
  /** VRF onde o cliente está roteado. */
  vrf: string | null;
  /** JSON array de portas/sub-interfaces (parse client-side). */
  interfaces_json: string | null;
  /** Score reputacional 0..100 (default 100, recalculado por janela 90d). */
  score_reputacional: number;
  /** ISO UTC do último recálculo do score. null = nunca calculado. */
  score_atualizado_em: string | null;
  criado_em: string;
  atualizado_em: string;
}

export interface ClienteEmail {
  id: string;
  tipo: "notificacao" | "tecnico" | "financeiro" | "outro";
  email: string;
  nome_contato: string | null;
  principal: boolean;
  observacao: string | null;
}

export interface ClienteTelefone {
  id: string;
  tipo: "celular" | "whatsapp" | "fixo" | "outro";
  numero: string;
  nome_contato: string | null;
  principal: boolean;
  observacao: string | null;
}

export interface ClienteContato {
  id: string;
  nome: string;
  cargo: string | null;
  email: string | null;
  telefone: string | null;
  observacao: string | null;
}

export interface ClienteAnotacao {
  id: string;
  autor_id: string | null;
  texto: string;
  criado_em: string;
}

export interface ClienteDetalhe {
  ok: true;
  cliente: ClienteAdmin;
  emails: ClienteEmail[];
  telefones: ClienteTelefone[];
  contatos: ClienteContato[];
  anotacoes: ClienteAnotacao[];
}

export function listarClientes() {
  return apiFetch<{ ok: true; clientes: ClienteAdmin[] }>("/api/admin/clientes", {
    ehAdmin: true,
  });
}

// ===== Vínculos cliente ↔ CIDR =====

export interface VinculoPendente {
  cidr_sugerido: string;
  description_phpipam: string | null;
  phpipam_subnet_id: string | null;
  qtd_alertas: number;
  severidade_max: string;
  tipos: string[];
  ips_amostra: string[];
  qtd_ips: number;
  ultimo_alerta_em: string;
}

export interface Vinculo {
  id: string;
  cliente_id: string;
  cliente_nome: string | null;
  cidr: string;
  origem: "manual" | "phpipam" | "auto";
  phpipam_subnet_id: string | null;
  phpipam_customer_id: string | null;
  observacao: string | null;
  criado_em: string;
  vigencia_inicio: string | null;
  vigencia_fim: string | null;
}

export function listarVinculosPendentes() {
  return apiFetch<{ ok: true; pendentes: VinculoPendente[] }>(
    "/api/admin/vinculos/pendentes",
    { ehAdmin: true },
  );
}

export function listarVinculos(
  cliente_id?: string,
  opts: { historico?: boolean } = {},
) {
  const params: string[] = [];
  if (cliente_id) params.push(`cliente_id=${encodeURIComponent(cliente_id)}`);
  if (opts.historico) params.push("historico=true");
  const qs = params.length > 0 ? `?${params.join("&")}` : "";
  return apiFetch<{ ok: true; vinculos: Vinculo[] }>(`/api/admin/vinculos${qs}`, {
    ehAdmin: true,
  });
}

export function criarVinculo(payload: {
  cliente_id: string;
  cidr: string;
  origem?: "manual" | "phpipam" | "auto";
  observacao?: string;
  phpipam_subnet_id?: string;
  phpipam_customer_id?: string;
  sincronizar_phpipam?: boolean;
}) {
  return apiFetch<{
    ok: true;
    vinculo: { id: string; cliente_id: string; cidr: string };
    alertas_retroativos: number;
    ips_retroativos: string[];
    phpipam_sync?: {
      tentado: boolean;
      customer_criado: string | null;
      customer_existente: string | null;
      subnet_atualizada: string | null;
      erro: string | null;
    };
  }>("/api/admin/vinculos", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

export function deletarVinculo(id: string) {
  return apiFetch<{ ok: true; phpipam_erro: string | null }>(`/api/admin/vinculos/${id}`, {
    method: "DELETE",
    ehAdmin: true,
  });
}

// ===== IXC integration =====

export interface ClienteIxcResumo {
  id: string;
  razao: string;
  fantasia: string | null;
  cnpj_cpf: string | null;
  tipo_pessoa: string | null;
  cidade: string | null;
  uf: string | null;
  ativo: string | null;
  email: string | null;
  telefone: string | null;
  celular: string | null;
}

export function buscarClientesIxc(termo: string) {
  return apiFetch<{
    ok: true;
    total: number;
    duracao_ms: number;
    registros: ClienteIxcResumo[];
  }>(`/api/admin/clientes/_ixc/buscar?q=${encodeURIComponent(termo)}`, { ehAdmin: true });
}

export interface ClienteIxcCompleto {
  id: string;
  razao: string;
  fantasia?: string;
  cnpj_cpf?: string;
  tipo_pessoa?: string;
  email?: string;
  telefone?: string;
  celular?: string;
  endereco?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  cep?: string;
  [k: string]: unknown;
}

export interface PreviewClienteIxc {
  ok: true;
  cliente: ClienteIxcCompleto;
  emails: Array<{ id: string; email: string; obs?: string; [k: string]: unknown }>;
  telefones: Array<{ id: string; numero: string; tipo?: string; whatsapp?: string; obs?: string; [k: string]: unknown }>;
  contatos: Array<{
    id: string;
    nome: string;
    funcao?: string;
    email?: string;
    telefone?: string;
    celular?: string;
    obs?: string;
    [k: string]: unknown;
  }>;
  erros: {
    emails: string | null;
    telefones: string | null;
    contatos: string | null;
  };
}

export function previewClienteIxc(idIxc: string) {
  return apiFetch<PreviewClienteIxc>(
    `/api/admin/clientes/_ixc/preview/${encodeURIComponent(idIxc)}`,
    { ehAdmin: true },
  );
}

// Criar cliente novo a partir do IXC (fluxo OBRIGATÓRIO de cadastro)
export function criarClienteDoIxc(payload: {
  id_ixc: string;
  criar_no_phpipam?: boolean;
  politica_notificacao?: "auto" | "manual" | "bloqueado";
}) {
  return apiFetch<{
    ok: true;
    cliente_id: string;
    nome: string;
    ja_existia?: boolean;
    emails_importados?: number;
    telefones_importados?: number;
    phpipam_customer_id: string | null;
    phpipam_erro: string | null;
  }>("/api/admin/clientes/criar-do-ixc", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

// ===== IPs individuais (clientes_ips) =====

export interface ClienteIpRow {
  id: string;
  cliente_id: string;
  ip: string;
  versao: number;
  origem: "manual" | "phpipam" | "ixc" | "auto";
  phpipam_address_id: string | null;
  phpipam_subnet_id: string | null;
  descricao: string | null;
  observacao: string | null;
  criado_em: string;
  vigencia_inicio: string | null;
  vigencia_fim: string | null;
}

export function listarIpsCliente(clienteId: string, opts: { historico?: boolean } = {}) {
  const qs = opts.historico ? "?historico=true" : "";
  return apiFetch<{ ok: true; ips: ClienteIpRow[] }>(
    `/api/admin/clientes/${clienteId}/ips${qs}`,
    { ehAdmin: true },
  );
}

// ===== Histórico cronológico de endereço (IP ou CIDR) =====

export interface ItemHistoricoEndereco {
  tipo: "ip" | "subnet";
  id: string;
  cliente_id: string;
  cliente_nome: string | null;
  cliente_documento: string | null;
  alvo: string;
  origem: string;
  observacao: string | null;
  vigencia_inicio: string | null;
  vigencia_fim: string | null;
  criado_em: string;
}

export function historicoEndereco(termo: string) {
  return apiFetch<{
    ok: true;
    termo: string;
    ehCidr: boolean;
    total: number;
    historico: ItemHistoricoEndereco[];
  }>(`/api/admin/clientes/_historico-endereco?q=${encodeURIComponent(termo)}`, {
    ehAdmin: true,
  });
}

// ===== Central de verificações de resolução =====

export interface VerificacaoListItem {
  id: string;
  alerta_id: string;
  incidente_id: string;
  cliente_id: string;
  cliente_nome: string | null;
  ip: string;
  porta: number | null;
  protocolo: string | null;
  tipo_alerta: string;
  status: "pendente" | "em_verificacao" | "resolvido" | "cancelado";
  agendado_para: string;
  tentativas: number;
  ultima_verificacao_em: string | null;
  ultima_verificacao_metodo: string | null;
  ultima_verificacao_resultado_json: string | null;
  resolvido_em: string | null;
  resolvido_metodo: string | null;
  resolvido_por: string | null;
  carta_id: string | null;
  carta_agradecimento_id: string | null;
  criado_em: string;
}

export function listarVerificacoes(filtros: {
  status?: string[];
  cliente_id?: string;
  tipo_alerta?: string;
  limit?: number;
} = {}) {
  const params: string[] = [];
  if (filtros.status?.length) params.push(`status=${filtros.status.join(",")}`);
  if (filtros.cliente_id) params.push(`cliente_id=${encodeURIComponent(filtros.cliente_id)}`);
  if (filtros.tipo_alerta) params.push(`tipo_alerta=${encodeURIComponent(filtros.tipo_alerta)}`);
  if (filtros.limit) params.push(`limit=${filtros.limit}`);
  const qs = params.length ? `?${params.join("&")}` : "";
  return apiFetch<{
    ok: true;
    verificacoes: VerificacaoListItem[];
    contagens: Record<string, number>;
  }>(`/api/admin/verificacoes${qs}`, { ehAdmin: true });
}

export function listarVerificacoesCliente(clienteId: string) {
  return apiFetch<{
    ok: true;
    verificacoes: VerificacaoListItem[];
    contagens: Record<string, number>;
  }>(`/api/admin/verificacoes/cliente/${clienteId}`, { ehAdmin: true });
}

export interface VerificacaoDetalhe {
  ok: true;
  verificacao: VerificacaoListItem;
  cliente: { id: string; nome: string; documento: string | null } | null;
  alerta: {
    id: string;
    tipo_alerta: string;
    severidade: string;
    asn: string;
    status: string;
    received_at: string;
    test_date: string | null;
    raw_excerpt: string | null;
    fonte: string;
    incidente_id: string;
  } | null;
  alerta_ips: Array<{
    id: string;
    ip: string;
    porta: number | null;
    protocolo: string | null;
    test_status: string | null;
    test_result: string | null;
  }>;
  cartas: Array<{
    id: string;
    assunto: string;
    corpo: string;
    status: string;
    enviada_em: string | null;
    enviada_para_json: string | null;
    criado_em: string;
  }>;
  carta_notificacao: VerificacaoDetalhe["cartas"][number] | null;
  carta_agradecimento: VerificacaoDetalhe["cartas"][number] | null;
  ultimo_resultado: null | {
    probe: { aberto: boolean | null; detalhes: string; metodo: string; duracao_ms: number; porta_probada?: number; estado_nmap?: string };
    alertas_recentes: number;
    janela_horas: number;
    decisao: { resolvido: boolean; metodo: string; detalhes: string };
  };
}

export function obterVerificacao(id: string) {
  return apiFetch<VerificacaoDetalhe>(`/api/admin/verificacoes/${id}`, {
    ehAdmin: true,
  });
}

export interface ResultadoSimulacao {
  resolvido: boolean;
  metodo: string;
  detalhes: string;
  probe: { aberto: boolean | null; detalhes: string; metodo: string; duracao_ms: number };
  alertas_recentes: number;
  janela_horas: number;
  agora: string;
}

/** Roda probe sem aplicar — operador vê o resultado e decide. */
export function simularVerificacao(verificacaoId: string) {
  return apiFetch<{ ok: true; resultado: ResultadoSimulacao }>(
    `/api/admin/verificacoes/${verificacaoId}/simular`,
    { method: "POST", ehAdmin: true },
  );
}

/** Aplica resultado de uma simulação. */
export function aplicarResultadoSimulacao(verificacaoId: string, resultado: ResultadoSimulacao) {
  return apiFetch<{ ok: true }>(`/api/admin/verificacoes/${verificacaoId}/aplicar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ resultado }),
  });
}

export function testarVerificacao(verificacaoId: string) {
  return apiFetch<{
    ok: true;
    resultado: {
      resolvido: boolean;
      metodo: string;
      detalhes: string;
      probe: { aberto: boolean | null; detalhes: string; metodo: string; duracao_ms?: number; porta_probada?: number; estado_nmap?: string };
      alertas_recentes: number;
      janela_horas: number;
    };
  }>(`/api/admin/verificacoes/${verificacaoId}/testar`, {
    method: "POST",
    ehAdmin: true,
  });
}

export function resolverVerificacao(verificacaoId: string, observacao?: string) {
  return apiFetch<{
    ok: true;
    carta_agradecimento_id: string | null;
  }>(`/api/admin/verificacoes/${verificacaoId}/resolver`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ observacao }),
  });
}

// ===== Timeline cronológica do incidente (a partir de um alerta) =====

export interface EventoTimeline {
  quando: string;
  tipo: string;
  titulo: string;
  detalhes: string | null;
  metadata?: Record<string, unknown>;
}

export function historicoAlerta(alertaId: string) {
  return apiFetch<{
    ok: true;
    alerta_id: string;
    incidente_id: string;
    total_eventos: number;
    eventos: EventoTimeline[];
  }>(`/api/admin/alertas/${alertaId}/historico`, { ehAdmin: true });
}

/**
 * Endpoint smart no backend: detecta IP individual vs CIDR pelo `/`.
 * - IP → cria em clientes_ips + address no phpIPAM
 * - CIDR → cria em clientes_subnets + PATCH subnet.customer_id no phpIPAM,
 *   só se a subnet de lá for exatamente a faixa (desde 11/09/2026)
 */
export interface ResultadoRetroatividadeApi {
  alertas_corrigidos: number;
  ips_corrigidos: string[];
  incidentes_reabertos: number;
  cartas_geradas: number;
  cartas_puladas_ja_existem: number;
  cartas_erros: Array<{ alerta_id: string; erro: string }>;
}

export function adicionarIpCliente(
  clienteId: string,
  payload: { ip: string; descricao?: string; observacao?: string; sincronizar_phpipam?: boolean },
) {
  return apiFetch<
    | {
        ok: true;
        tipo: "ip";
        ip: { id: string; ip: string; versao: number; phpipam_address_id: string | null };
        phpipam_erro: string | null;
        retroatividade: ResultadoRetroatividadeApi | null;
        /** Alertas do IP já com OUTRO cliente — a retroatividade não os move. */
        alertas_com_outro_cliente: number | null;
      }
    | {
        ok: true;
        tipo: "subnet";
        subnet: { id: string; cidr: string; phpipam_subnet_id: string | null };
        phpipam_erro: string | null;
        retroatividade: ResultadoRetroatividadeApi | null;
        alertas_com_outro_cliente: number | null;
      }
  >(`/api/admin/clientes/${clienteId}/ips`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

/**
 * Dispara reprocessamento retroativo: pra todos os vínculos (CIDRs + IPs)
 * do cliente, revarre alertas em aguardando_vinculo e gera cartas.
 * Idempotente — pula cartas que já existem.
 */
export function reprocessarPendentes(clienteId: string) {
  return apiFetch<{
    ok: true;
    total: ResultadoRetroatividadeApi;
    qtd_vinculos: number;
    detalhes: Array<{
      tipo: "cidr" | "ip";
      alvo: string;
      resultado: ResultadoRetroatividadeApi;
    }>;
  }>(`/api/admin/clientes/${clienteId}/reprocessar-pendentes`, {
    method: "POST",
    ehAdmin: true,
  });
}

/**
 * Reatribui alertas EXISTENTES (já vinculados a OUTRO cliente) pro cliente
 * alvo. Usado quando o operador adiciona IP/CIDR mais específico que
 * sobrescreve um vínculo anterior (ex: /32 ganhando de /24 antigo).
 *
 * DESTRUTIVO — confirma com o operador antes de chamar.
 */
/** Filtro de reatribuição: IP exato ou faixa (o servidor normaliza o CIDR). */
export type FiltroReatribuir = { tipo: "ip"; ip: string } | { tipo: "faixa"; cidr: string };

export interface AlertaDeOutroClienteApi {
  alerta_id: string;
  alerta_ip_id: string;
  ip: string;
  ocorrido_em: string | null;
  tipo_alerta: string | null;
  severidade: string | null;
  cliente_atual_id: string;
  cliente_atual_nome: string | null;
  incidente_id_humano: string | null;
}

/** Alertas do filtro que estão com OUTRO cliente — somente leitura. */
export function previaReatribuirAlertas(clienteId: string, filtro: FiltroReatribuir) {
  return apiFetch<{ ok: true; alertas: AlertaDeOutroClienteApi[] }>(
    `/api/admin/clientes/${clienteId}/reatribuir-alertas/previa`,
    { method: "POST", ehAdmin: true, body: JSON.stringify({ filtro }) },
  );
}

/**
 * Move os alertas ESCOLHIDOS (`alertaIds`) para o cliente alvo. Desde
 * 11/09/2026 a lista é obrigatória — o vínculo novo diz quem é o dono hoje,
 * não quem era quando cada alerta chegou.
 */
export function reatribuirAlertasCliente(
  clienteId: string,
  filtro: FiltroReatribuir,
  alertaIds: string[],
) {
  return apiFetch<{ ok: true; total: ResultadoRetroatividadeApi }>(
    `/api/admin/clientes/${clienteId}/reatribuir-alertas`,
    {
      method: "POST",
      ehAdmin: true,
      body: JSON.stringify({ filtro, alerta_ids: alertaIds }),
    },
  );
}

// ─── Notificação manual / proativa ────────────────────────────────
// Caso de uso: operador descobre por fora (nmap/Shodan) que cliente tem
// porta vulnerável aberta e quer mandar a carta ANTES do CERT.br
// detectar. Cria alerta sintético (fonte=interno) e dispara o pipeline
// normal de geração de carta.

export type TipoAlertaProativo =
  | "tftp_aberto"
  | "dns_resolver_aberto"
  | "ntp_amplificador"
  | "smtp_open_relay"
  | "ssdp_aberto"
  | "snmp_aberto"
  | "mdns_aberto"
  | "coap_aberto"
  | "chargen_aberto"
  | "netbios_aberto"
  | "portmap_aberto"
  | "ldap_aberto"
  | "servidor_ssh_vulneravel"
  | "porta_admin_exposta"
  | "servico_exposto";

export interface PayloadNotificacaoManual {
  tipo_alerta: TipoAlertaProativo;
  ip: string;
  porta?: number | null;
  protocolo?: "tcp" | "udp" | null;
  severidade?: "baixa" | "media" | "alta" | "critica";
  observacao?: string;
}

export interface RespostaNotificacaoManual {
  ok: true;
  alerta_id: string;
  incidente_id: string;
  email_message_id: string;
  carta_id: string | null;
  modo_envio: "auto" | "manual";
  motivo_manual?: string;
  erro_carta?: string;
}

export function notificarClienteManual(
  clienteId: string,
  payload: PayloadNotificacaoManual,
) {
  return apiFetch<RespostaNotificacaoManual>(
    `/api/admin/clientes/${clienteId}/notificar-manual`,
    {
      method: "POST",
      ehAdmin: true,
      body: JSON.stringify(payload),
    },
  );
}

export function removerIpCliente(clienteId: string, ipId: string) {
  return apiFetch<{ ok: true; phpipam_erro: string | null }>(
    `/api/admin/clientes/${clienteId}/ips/${ipId}`,
    { method: "DELETE", ehAdmin: true },
  );
}

export function importarClienteIxc(
  clienteId: string,
  payload: {
    id_ixc: string;
    emails_selecionados: string[];
    telefones_selecionados: string[];
    contatos_selecionados: Array<{
      nome: string;
      funcao?: string;
      email?: string;
      telefone?: string;
    }>;
    atualizar_dados_basicos?: boolean;
  },
) {
  return apiFetch<{
    ok: true;
    emails_importados: number;
    telefones_importados: number;
    contatos_importados: number;
    dados_atualizados: boolean;
  }>(`/api/admin/clientes/${clienteId}/importar-ixc`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

export function obterCliente(id: string) {
  return apiFetch<ClienteDetalhe>(`/api/admin/clientes/${id}`, { ehAdmin: true });
}

export interface PayloadNovoCliente {
  nome: string;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  documento?: string | null;
  contrato?: string | null;
  external_customer_id?: string | null;
  pop?: string | null;
  politica_notificacao?: "auto" | "manual" | "bloqueado";
  endereco_cep?: string | null;
  endereco_logradouro?: string | null;
  endereco_numero?: string | null;
  endereco_complemento?: string | null;
  endereco_bairro?: string | null;
  endereco_cidade?: string | null;
  endereco_uf?: string | null;
  tags?: string[];
  observacoes?: string | null;
  /** Marca cliente como provedor B2B (host atrás de NAT) */
  eh_provedor_b2b?: boolean;
  /** Janela em dias de ausência de novos alertas pra confirmar resolução (1-60) */
  janela_ausencia_dias?: number;
  /** ASN do cliente (0..2^32-1). null pra apagar. */
  asn?: number | null;
  /** VLAN default no PE (1..4094). null pra apagar. */
  vlan_default?: number | null;
  /** Nome do VRF onde o cliente está roteado. null pra apagar. */
  vrf?: string | null;
  /** Lista de portas/sub-interfaces. null pra apagar. */
  interfaces?: string[] | null;
}

export function criarCliente(dados: PayloadNovoCliente) {
  return apiFetch<{ ok: true; id: string }>("/api/admin/clientes", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(dados),
  });
}

export function atualizarCliente(id: string, dados: Partial<PayloadNovoCliente>) {
  return apiFetch<{ ok: true }>(`/api/admin/clientes/${id}`, {
    method: "PUT",
    ehAdmin: true,
    body: JSON.stringify(dados),
  });
}

export interface OfensorItem {
  cliente_id: string;
  nome: string;
  contrato: string | null;
  pop: string | null;
  score: number;
  eh_provedor_b2b: boolean;
  qtd_incidentes: number;
  qtd_reincidencias: number;
  ultima_severidade: string | null;
  ultimo_incidente_em: string | null;
}

/**
 * Top ofensores — clientes ordenados pelo pior score na janela.
 * janela_dias entre 7 e 365 (default 90). limit entre 5 e 100 (default 20).
 */
export function listarTopOfensores(opts: { janela_dias?: number; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (opts.janela_dias) qs.set("janela_dias", String(opts.janela_dias));
  if (opts.limit) qs.set("limit", String(opts.limit));
  const sufixo = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<{
    ok: true;
    janela_dias: number;
    total: number;
    ofensores: OfensorItem[];
  }>(`/api/admin/clientes/top-ofensores${sufixo}`, { ehAdmin: true });
}

export interface DiagnosticoBgpApi {
  asn: number;
  consultado_em: string;
  anunciados: string[];
  vinculados_locais: string[];
  somente_anunciados: string[];
  somente_vinculados: string[];
  rpki: Array<{
    prefix: string;
    origin: string;
    validity: "valid" | "invalid_asn" | "invalid_length" | "unknown" | "indisponivel";
    vrps?: Array<{ asn: string; prefix: string; max_length: number }>;
  }>;
  indisponivel: boolean;
  motivo?: string;
}

/**
 * Diagnóstico BGP/RPKI do cliente. Requer ASN preenchido no cadastro.
 * Erros possíveis: cliente_sem_asn (400), nao_encontrado (404).
 */
export function diagnosticarBgpCliente(id: string) {
  return apiFetch<{ ok: true; diagnostico: DiagnosticoBgpApi }>(
    `/api/admin/clientes/${id}/bgp`,
    { ehAdmin: true },
  );
}

/**
 * Reenvia o e-mail de boas-vindas pro cliente. Vai pra TODOS os contatos
 * cadastrados (notificação + técnico) com CC noc@usetelecom.com.br.
 *
 * Códigos de erro possíveis:
 *  - 404 cliente_nao_encontrado
 *  - 422 sem_emails_cadastrados (cadastrar contatos antes)
 *  - 429 rate_limit (5/h/ator)
 *  - 502 envio_falhou (Resend offline)
 */
export function reenviarBoasVindasCliente(id: string) {
  return apiFetch<{
    ok: true;
    destinatarios: string[];
    cc: string[];
  }>(`/api/admin/clientes/${id}/reenviar-boasvindas`, {
    method: "POST",
    ehAdmin: true,
  });
}

/**
 * Força recálculo imediato do score (ignora cache de 6h).
 * Retorna o novo score + estatísticas da janela considerada (90d).
 */
export function recalcularScoreCliente(id: string) {
  return apiFetch<{
    ok: true;
    score: number;
    total_incidentes_janela: number;
    total_reincidencias_janela: number;
    janela_dias: number;
    calculado_em: string;
  }>(`/api/admin/clientes/${id}/recalcular-score`, {
    method: "POST",
    ehAdmin: true,
  });
}

export interface NovoEmailPayload {
  tipo: ClienteEmail["tipo"];
  email: string;
  nome_contato?: string | null;
  observacao?: string | null;
  principal?: boolean;
}
export function adicionarEmail(clienteId: string, dados: NovoEmailPayload) {
  return apiFetch<{ ok: true; id: string }>(`/api/admin/clientes/${clienteId}/emails`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(dados),
  });
}

export function removerEmail(clienteId: string, emailId: string) {
  return apiFetch<{ ok: true }>(`/api/admin/clientes/${clienteId}/emails/${emailId}`, {
    method: "DELETE",
    ehAdmin: true,
  });
}

export function editarEmail(
  clienteId: string,
  emailId: string,
  patch: Partial<NovoEmailPayload>,
) {
  return apiFetch<{ ok: true }>(`/api/admin/clientes/${clienteId}/emails/${emailId}`, {
    method: "PATCH",
    ehAdmin: true,
    body: JSON.stringify(patch),
  });
}

export interface NovoTelefonePayload {
  tipo: ClienteTelefone["tipo"];
  numero: string;
  nome_contato?: string | null;
  observacao?: string | null;
  principal?: boolean;
}
export function adicionarTelefone(clienteId: string, dados: NovoTelefonePayload) {
  return apiFetch<{ ok: true; id: string }>(`/api/admin/clientes/${clienteId}/telefones`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(dados),
  });
}

export function removerTelefone(clienteId: string, telId: string) {
  return apiFetch<{ ok: true }>(`/api/admin/clientes/${clienteId}/telefones/${telId}`, {
    method: "DELETE",
    ehAdmin: true,
  });
}

export function editarTelefone(
  clienteId: string,
  telId: string,
  patch: Partial<NovoTelefonePayload>,
) {
  return apiFetch<{ ok: true }>(`/api/admin/clientes/${clienteId}/telefones/${telId}`, {
    method: "PATCH",
    ehAdmin: true,
    body: JSON.stringify(patch),
  });
}

export interface NovoContatoPayload {
  nome: string;
  cargo?: string | null;
  email?: string | null;
  telefone?: string | null;
  observacao?: string | null;
}
export function adicionarContato(clienteId: string, dados: NovoContatoPayload) {
  return apiFetch<{ ok: true; id: string }>(`/api/admin/clientes/${clienteId}/contatos`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(dados),
  });
}

export function removerContato(clienteId: string, contatoId: string) {
  return apiFetch<{ ok: true }>(`/api/admin/clientes/${clienteId}/contatos/${contatoId}`, {
    method: "DELETE",
    ehAdmin: true,
  });
}

export function adicionarAnotacao(clienteId: string, texto: string) {
  return apiFetch<{ ok: true; id: string }>(`/api/admin/clientes/${clienteId}/anotacoes`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ texto }),
  });
}

// ===== phpIPAM autocomplete (subnets) =====

export interface PhpipamSubnetSugestao {
  id: string;
  cidr: string;
  subnet: string;
  mask: string;
  description: string | null;
  customer_id: string | null;
}

export function buscarEnderecosPhpipam(termo: string) {
  return apiFetch<{
    ok: true;
    duracao_ms: number;
    subnets: PhpipamSubnetSugestao[];
  }>(`/api/admin/clientes/_phpipam/buscar-endereco?q=${encodeURIComponent(termo)}`, {
    ehAdmin: true,
  });
}

export function syncPhpipam(clienteId: string, ip: string) {
  return apiFetch<{ ok: true; encontrado?: boolean; payload?: unknown; duracao_ms: number; erro?: string }>(
    `/api/admin/clientes/${clienteId}/sync-phpipam`,
    {
      method: "POST",
      ehAdmin: true,
      body: JSON.stringify({ ip }),
    },
  );
}

export interface ConfiguracaoLinha {
  chave: string;
  valor: string;
  descricao: string | null;
}
export function listarConfiguracoes() {
  return apiFetch<{ ok: true; configuracoes: ConfiguracaoLinha[] }>(
    "/api/admin/configuracoes",
    { ehAdmin: true },
  );
}

export function atualizarConfiguracao(chave: string, valor: string) {
  return apiFetch<{ ok: true }>(`/api/admin/configuracoes/${chave}`, {
    method: "PUT",
    body: JSON.stringify({ valor }),
    ehAdmin: true,
  });
}

// ===== E-mails capturados =====

export interface EmailCapturado {
  id: string;
  gmail_message_id: string;
  thread_id: string | null;
  from_email: string;
  subject: string;
  received_at: string;
  parser_status: "pendente" | "ok" | "fuzzy" | "falhou";
  parser_erro: string | null;
  attachment_count: number;
  processed_at: string | null;
  criado_em: string;
  qtd_alertas: number;
}

export function listarEmailsCapturados() {
  return apiFetch<{ ok: true; emails: EmailCapturado[] }>("/api/admin/emails", {
    ehAdmin: true,
  });
}

export interface DetalheEmail {
  ok: true;
  email: EmailCapturado & {
    raw_r2_key: string | null;
    parsed_text: string | null;
  };
  alertas: Array<{
    id: string;
    tipo_alerta: string;
    severidade: string;
    status: string;
    asn: string;
    test_date: string | null;
    raw_excerpt: string | null;
  }>;
  ips: Array<{
    id: string;
    alerta_id: string;
    ip: string;
    protocolo: string | null;
    porta: number | null;
    test_status: string | null;
    cliente_id: string | null;
    lookup_status: string;
  }>;
  cartas: Array<{
    id: string;
    assunto: string;
    status: string;
    modo_envio: string;
    enviada_em: string | null;
    criado_em: string;
  }>;
}

export function obterEmail(id: string) {
  return apiFetch<DetalheEmail>(`/api/admin/emails/${id}`, { ehAdmin: true });
}

export function obterRawEmail(id: string) {
  return apiFetch<{ ok: true; conteudo: string }>(`/api/admin/emails/${id}/raw`, {
    ehAdmin: true,
  });
}

/**
 * Reprocessa em LOTE todos os emails com `parser_status='pendente'`.
 * Útil pós-deploy de parser novo + reset manual.
 *
 *  - filtro_subject: SQL LIKE pattern (ex: '[Sightline]%') pra escopo
 *  - max: limite hard (default 50, máx 100)
 *
 * Nota: nome `reprocessarEmailsPendentes` pra não conflitar com a função
 * homônima do domínio cliente (`reprocessarPendentes(clienteId)`).
 */
export function reprocessarEmailsPendentes(
  opts: { filtro_subject?: string; max?: number } = {},
) {
  return apiFetch<{
    ok: true;
    total: number;
    processados: number;
    falhas: number;
    detalhes: Array<{
      id: string;
      subject: string;
      parser_status: string;
      alertas: number;
      cartas: number;
      erro?: string;
    }>;
  }>("/api/admin/emails/reprocessar-pendentes", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(opts),
  });
}

/**
 * Reprocessa um email que já está no D1 (sem puxar do Gmail). Útil quando
 * o parser foi atualizado depois do email ter sido processado.
 *
 *  - resultado.alertas_novos / cartas_geradas / parser_status
 *  - resultado.erro se algo deu errado no parser
 */
export function reprocessarEmail(id: string, opts: { reset_arbor?: boolean } = {}) {
  return apiFetch<{
    ok: true;
    resultado: {
      email_message_id: string;
      alertas_novos: number;
      alertas_duplicados: number;
      cartas_geradas: number;
      parser_status: string;
      erro?: string;
    };
  }>(`/api/admin/emails/${id}/reprocessar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ reset_arbor: opts.reset_arbor ?? false }),
  });
}

// ===== Status do cron =====

export interface UltimaExecucaoCron {
  iniciada_em: string;
  terminada_em: string;
  duracao_ms: number;
  status: "ok" | "erro";
  mensagens_processadas: number;
  cartas_geradas: number;
  alertas_novos: number;
  alertas_duplicados: number;
  erro?: string;
  trigger: "cron" | "manual";
}

export function obterUltimaExecucaoCron() {
  return apiFetch<{ ok: true; ultima_execucao: UltimaExecucaoCron | null }>(
    "/api/admin/emails/_meta/ultima-execucao",
    { ehAdmin: true },
  );
}

// ===== Dashboard agregado =====

export interface DashboardDados {
  ok: true;
  contadores: {
    alertas_hoje: number;
    alertas_7d: number;
    alertas_30d: number;
    alertas_total: number;
    alertas_sem_cliente: number;
    incidentes_abertos: number;
    /** Alertas que pertencem a esses incidentes: o total da lista que o cartão abre. */
    alertas_de_incidentes_abertos?: number;
    incidentes_resolvidos_30d: number;
    incidentes_aguardando_identificacao: number;
    cartas_pendentes_aprovacao: number;
    cartas_enviadas_hoje: number;
    cartas_enviadas_7d: number;
    cartas_falha_envio: number;
    verificacoes_pendentes: number;
    verificacoes_resolvidas: number;
    verificacoes_canceladas: number;
    vinculos_ativos: number;
    clientes_total: number;
  };
  series: {
    alertas_14d: Array<{ data: string; qtd: number; criticos: number }>;
    cartas_14d: Array<{ data: string; enviadas: number; falhas: number }>;
    resolucoes_14d: Array<{ data: string; qtd: number }>;
  };
  top: {
    tipos_alerta_30d: Array<{ tipo: string; qtd: number }>;
    clientes_30d: Array<{ cliente_id: string; cliente_nome: string; qtd: number }>;
    severidade_7d: Array<{ severidade: string; qtd: number }>;
    /** Severidade nas últimas 24h — card grande no Dashboard */
    severidade_24h: Array<{ severidade: string; qtd: number }>;
  };
  saude: {
    ultima_execucao_cron: UltimaExecucaoCron | null;
    mttr_horas: number | null;
    taxa_resolucao_30d: number;
    taxa_auto_30d: number;
    total_cartas_30d: number;
    incidentes_criados_30d: number;
  };
  acionaveis: {
    cartas_aguardando_aprovacao: Array<{
      id: string;
      assunto: string;
      criado_em: string;
      cliente_nome: string | null;
    }>;
    /** Alertas críticos/altos há > 24h sem carta enviada — risco SLA CERT.br */
    sla_vermelho: Array<{
      id: string;
      tipo_alerta: string;
      severidade: string;
      received_at: string;
      horas_decorridas: number;
    }>;
    /** Contagem de alertas críticos/altos em janela 12h–24h (aviso) */
    sla_ambar_qtd: number;
  };
}

export function obterDashboard() {
  return apiFetch<DashboardDados>("/api/admin/dashboard", { ehAdmin: true });
}

// ===== Probe externo (VPS nmap) =====

export interface ProbeChamada {
  ts: number;
  ip: string;
  porta: number | null;
  protocolo: string;
  aberto: boolean | null;
  estado_nmap?: string;
  duracao_ms: number;
  ok: boolean;
  erro?: string;
}

export interface ProbeStats {
  ok: true;
  online: boolean;
  fonte?: "cache" | "live";
  servico?: string;
  versao?: string;
  uptime_segundos?: number;
  iniciado_em?: number;
  total_probes?: number;
  probes_aberto?: number;
  probes_fechado?: number;
  probes_inconclusivo?: number;
  probes_erro?: number;
  latencia_media_ms?: number;
  ultimas_chamadas?: ProbeChamada[];
  erro?: string;
  detalhes?: string;
}

export function obterProbeStats() {
  return apiFetch<ProbeStats>("/api/admin/probe/stats", { ehAdmin: true });
}

export interface ProbeHistoricoItem {
  verificacao_id: string;
  alerta_id: string;
  cliente_id: string | null;
  cliente_nome: string | null;
  ip: string;
  porta: number | null;
  protocolo: string | null;
  tipo_alerta: string;
  status: string;
  tentativas: number;
  executado_em: string;
  metodo: string | null;
  probe: {
    aberto: boolean | null;
    detalhes: string;
    metodo: string;
    duracao_ms?: number;
    estado_nmap?: string;
    porta_probada?: number;
  } | null;
  alertas_recentes: number | null;
  janela_horas: number | null;
  decisao: { resolvido: boolean; metodo: string; detalhes: string } | null;
}

export function obterProbeHistorico(limit = 200) {
  return apiFetch<{
    ok: true;
    total: number;
    contagens: {
      aberto: number;
      fechado: number;
      inconclusivo: number;
      via_vps: number;
      via_worker: number;
    };
    historico: ProbeHistoricoItem[];
  }>(`/api/admin/probe/historico?limit=${limit}`, { ehAdmin: true });
}

// ===== Usuários =====

export interface UsuarioAdmin {
  id: string;
  email: string;
  nome: string;
  role: import("@sentinela/shared/permissoes").Role;
  ativo: boolean;
  ultimo_login_em: string | null;
  criado_em: string;
  atualizado_em: string;
}

export function listarUsuarios() {
  return apiFetch<{ ok: true; usuarios: UsuarioAdmin[] }>("/api/admin/usuarios", {
    ehAdmin: true,
  });
}

export function criarUsuario(payload: {
  email: string;
  nome: string;
  role: UsuarioAdmin["role"];
}) {
  return apiFetch<{ ok: true; id: string; aviso_cf_access: string }>(
    "/api/admin/usuarios",
    { method: "POST", ehAdmin: true, body: JSON.stringify(payload) },
  );
}

export function editarUsuario(
  id: string,
  patch: Partial<{ nome: string; role: UsuarioAdmin["role"]; ativo: boolean }>,
) {
  return apiFetch<{ ok: true }>(`/api/admin/usuarios/${id}`, {
    method: "PATCH",
    ehAdmin: true,
    body: JSON.stringify(patch),
  });
}

export function deletarUsuario(id: string) {
  return apiFetch<{ ok: true }>(`/api/admin/usuarios/${id}`, {
    method: "DELETE",
    ehAdmin: true,
  });
}

export interface SessaoUsuarioItem {
  id: string;
  jti: string;
  iniciada_em: string;
  ultima_atividade_em: string;
  encerrada_em: string | null;
  motivo_encerramento: string | null;
  ip: string | null;
  user_agent: string | null;
  /** Computed: sem encerrada_em E última atividade < 8h atrás */
  ativa: boolean;
  /** Computed: (encerrada_em || ultima_atividade_em) - iniciada_em em segundos */
  duracao_segundos: number;
}

export type OrdemSessao =
  | "iniciada_em_desc"
  | "iniciada_em_asc"
  | "ultima_atividade_desc"
  | "ultima_atividade_asc";

export type StatusSessaoFiltro = "ativa" | "encerrada" | "todas";

export function listarSessoesUsuario(
  usuarioId: string,
  opts: {
    status?: StatusSessaoFiltro;
    desde?: string;
    ate?: string;
    ordenar_por?: OrdemSessao;
    pagina?: number;
    limit?: number;
  } = {},
) {
  const params: string[] = [];
  if (opts.status && opts.status !== "todas") params.push(`status=${opts.status}`);
  if (opts.desde) params.push(`desde=${opts.desde}`);
  if (opts.ate) params.push(`ate=${opts.ate}`);
  if (opts.ordenar_por) params.push(`ordenar_por=${opts.ordenar_por}`);
  if (opts.pagina && opts.pagina > 1) params.push(`pagina=${opts.pagina}`);
  if (opts.limit) params.push(`limit=${opts.limit}`);
  const qs = params.length ? `?${params.join("&")}` : "";
  return apiFetch<{
    ok: true;
    usuario: { id: string; nome: string; email: string };
    sessoes: SessaoUsuarioItem[];
    qtd_total: number;
    pagina: number;
    limit: number;
    total_paginas: number;
  }>(`/api/admin/usuarios/${usuarioId}/sessoes${qs}`, { ehAdmin: true });
}

export function encerrarSessaoUsuario(usuarioId: string, sessaoId: string) {
  return apiFetch<{ ok: true; encerrada_em: string }>(
    `/api/admin/usuarios/${usuarioId}/sessoes/${sessaoId}/encerrar`,
    { method: "POST", ehAdmin: true },
  );
}

export function reenviarBoasVindasUsuario(id: string) {
  return apiFetch<{
    ok: true;
    resend_id: string;
    duracao_ms: number;
    destinatario: string;
  }>(`/api/admin/usuarios/${id}/reenviar-boas-vindas`, {
    method: "POST",
    ehAdmin: true,
  });
}

// ===== Auditoria =====

export interface AuditoriaEvento {
  id: string;
  ator_id: string | null;
  ator_tipo: string;
  ator_nome: string | null;
  ator_email: string | null;
  acao: string;
  entidade_tipo: string;
  entidade_id: string | null;
  metadata_json: string | null;
  ip: string | null;
  user_agent: string | null;
  criado_em: string;
}

export function listarAuditoria(opts: {
  pagina?: number;
  limit?: number;
  ator_id?: string;
  entidade_tipo?: string;
  acao?: string;
  /** Busca ampla: ator (e-mail ou nome), ação, tipo e id da entidade, metadados. */
  q?: string;
  /** Dia da Bahia, AAAA-MM-DD, inclusivos. */
  de?: string;
  ate?: string;
  ordenar?: "criado_em" | "acao" | "entidade_tipo";
  dir?: "asc" | "desc";
} = {}) {
  const params: string[] = [];
  if (opts.pagina && opts.pagina > 1) params.push(`pagina=${opts.pagina}`);
  if (opts.limit) params.push(`limit=${opts.limit}`);
  if (opts.ator_id) params.push(`ator_id=${encodeURIComponent(opts.ator_id)}`);
  if (opts.entidade_tipo)
    params.push(`entidade_tipo=${encodeURIComponent(opts.entidade_tipo)}`);
  if (opts.acao) params.push(`acao=${encodeURIComponent(opts.acao)}`);
  if (opts.q) params.push(`q=${encodeURIComponent(opts.q)}`);
  if (opts.de) params.push(`de=${opts.de}`);
  if (opts.ate) params.push(`ate=${opts.ate}`);
  if (opts.ordenar) params.push(`ordenar=${opts.ordenar}`);
  if (opts.dir) params.push(`dir=${opts.dir}`);
  const qs = params.length ? `?${params.join("&")}` : "";
  return apiFetch<{
    ok: true;
    eventos: AuditoriaEvento[];
    qtd_total: number;
    pagina: number;
    limit: number;
    total_paginas: number;
  }>(`/api/admin/auditoria${qs}`, { ehAdmin: true });
}

/** Tipos de entidade que existem no log (o filtro "Entidade" não é mais uma lista escrita à mão; DO-12). */
export function listarEntidadesAuditoria() {
  return apiFetch<{ ok: true; entidades: string[] }>("/api/admin/auditoria/entidades", {
    ehAdmin: true,
  });
}

export function listarAcoesAuditoria() {
  return apiFetch<{ ok: true; acoes: string[] }>("/api/admin/auditoria/acoes", {
    ehAdmin: true,
  });
}

// ─── Dashboard ranking de atividade (apenas master) ──────────────────
export interface RankingAtor {
  ator_id: string | null;
  ator_nome: string;
  ator_email: string | null;
  ator_role: string | null;
  ator_ativo: number;
  qtd_total: number;
  qtd_30d: number;
  qtd_7d: number;
  ultima_acao: string;
}

export interface AcaoUsuario {
  acao: string;
  qtd: number;
}

export interface AtividadeDiaria {
  dia: string;
  qtd_acoes: number;
  qtd_usuarios: number;
}

export interface RankingResposta {
  ok: true;
  kpis: {
    total_tudo: number;
    total_30d: number;
    total_7d: number;
    usuarios_ativos_30d: number;
    usuarios_ativos_7d: number;
  };
  ranking: RankingAtor[];
  acoes_por_usuario: Record<string, AcaoUsuario[]>;
  atividade_diaria: AtividadeDiaria[];
  janela: { corte_7d: string; corte_30d: string; agora: string };
}

export function obterRankingAuditoria() {
  return apiFetch<RankingResposta>("/api/admin/auditoria/ranking", {
    ehAdmin: true,
  });
}

// ─── ChangeLog (apenas master) ────────────────────────────────────────
export type CategoriaChangelog =
  | "bug_fix"
  | "feature"
  | "improvement"
  | "security"
  | "database"
  | "api"
  | "ui_ux"
  | "infrastructure"
  | "hotfix";

export type StatusChangelog =
  | "em_desenvolvimento"
  | "em_teste"
  | "publicado"
  | "revertido";

export interface ChangelogLink {
  tipo: "pr" | "commit" | "doc" | "link";
  url: string;
  label: string;
}

export interface ChangelogEntrada {
  id: string;
  titulo: string;
  categoria: CategoriaChangelog;
  descricao: string;
  impacto: string | null;
  modulo_afetado: string | null;
  responsavel_id: string | null;
  responsavel_email: string;
  versao: string;
  status: StatusChangelog;
  links_json: string | null;
  publicado_em: string | null;
  criado_em: string;
  atualizado_em: string;
  editado_por_id: string | null;
  editado_por_email: string | null;
}

export interface ListarChangelogOpts {
  categoria?: CategoriaChangelog;
  status?: StatusChangelog;
  versao?: string;
  responsavel_id?: string;
  q?: string;
  data_inicio?: string;
  data_fim?: string;
  pagina?: number;
  limit?: number;
}

export function listarChangelog(opts: ListarChangelogOpts = {}) {
  const params: string[] = [];
  if (opts.categoria) params.push(`categoria=${encodeURIComponent(opts.categoria)}`);
  if (opts.status) params.push(`status=${encodeURIComponent(opts.status)}`);
  if (opts.versao) params.push(`versao=${encodeURIComponent(opts.versao)}`);
  if (opts.responsavel_id)
    params.push(`responsavel_id=${encodeURIComponent(opts.responsavel_id)}`);
  if (opts.q) params.push(`q=${encodeURIComponent(opts.q)}`);
  if (opts.data_inicio)
    params.push(`data_inicio=${encodeURIComponent(opts.data_inicio)}`);
  if (opts.data_fim) params.push(`data_fim=${encodeURIComponent(opts.data_fim)}`);
  if (opts.pagina && opts.pagina > 1) params.push(`pagina=${opts.pagina}`);
  if (opts.limit) params.push(`limit=${opts.limit}`);
  const qs = params.length ? `?${params.join("&")}` : "";
  return apiFetch<{
    ok: true;
    entradas: ChangelogEntrada[];
    qtd_total: number;
    pagina: number;
    limit: number;
    total_paginas: number;
  }>(`/api/admin/changelog${qs}`, { ehAdmin: true });
}

export function obterChangelogEntrada(id: string) {
  return apiFetch<{ ok: true; entrada: ChangelogEntrada }>(
    `/api/admin/changelog/${id}`,
    { ehAdmin: true },
  );
}

// ─── Novidades (modal pós-login) ─────────────────────────────────────
// Qualquer usuário logado — entradas PUBLICADAS após o último "visto".

export interface NovidadeChangelog {
  id: string;
  titulo: string;
  categoria: string;
  descricao: string;
  impacto: string | null;
  modulo_afetado: string | null;
  versao: string;
  criado_em: string;
}

export function changelogNovidades() {
  return apiFetch<{ ok: true; marco: string; novidades: NovidadeChangelog[] }>(
    "/api/admin/changelog/_/novidades",
    { ehAdmin: true },
  );
}

export function changelogMarcarNovidadesVisto() {
  return apiFetch<{ ok: true }>("/api/admin/changelog/_/novidades/visto", {
    method: "POST",
    ehAdmin: true,
  });
}

export interface PayloadChangelog {
  titulo: string;
  categoria: CategoriaChangelog;
  descricao: string;
  impacto?: string | null;
  modulo_afetado?: string | null;
  versao: string;
  status?: StatusChangelog;
  links?: ChangelogLink[];
}

export function criarChangelogEntrada(payload: PayloadChangelog) {
  return apiFetch<{ ok: true; entrada: ChangelogEntrada }>(
    `/api/admin/changelog`,
    { method: "POST", ehAdmin: true, body: JSON.stringify(payload) },
  );
}

export function editarChangelogEntrada(
  id: string,
  payload: Partial<PayloadChangelog>,
) {
  return apiFetch<{ ok: true; entrada: ChangelogEntrada }>(
    `/api/admin/changelog/${id}`,
    { method: "PUT", ehAdmin: true, body: JSON.stringify(payload) },
  );
}

export function apagarChangelogEntrada(id: string) {
  return apiFetch<{ ok: true }>(`/api/admin/changelog/${id}`, {
    method: "DELETE",
    ehAdmin: true,
  });
}

export interface ChangelogAgregados {
  ok: true;
  total: number;
  por_categoria: Array<{ categoria: string; qtd: number }>;
  por_status: Array<{ status: string; qtd: number }>;
  por_versao: Array<{ versao: string; qtd: number; ultima: string }>;
}

export function obterAgregadosChangelog() {
  return apiFetch<ChangelogAgregados>("/api/admin/changelog/_/agregados", {
    ehAdmin: true,
  });
}

export function listarVersoesChangelog() {
  return apiFetch<{ ok: true; versoes: string[] }>(
    "/api/admin/changelog/_/versoes",
    { ehAdmin: true },
  );
}

/**
 * Reenvia uma carta já enviada (ou falhada) pros contatos ATUAIS do cliente.
 * Útil quando operador adiciona email novo após o envio original e precisa
 * alcançar o novo contato.
 *
 * Não cria carta nova — reusa a mesma cartas.id, atualiza enviada_em e
 * enviada_para_json. Backend re-lê clientes_emails na hora.
 */
export function reenviarCarta(cartaId: string, motivo?: string) {
  return apiFetch<{
    ok: true;
    destinatarios: string[];
    cc: string[];
    adicionados: string[];
    removidos: string[];
  }>(`/api/admin/cartas/${cartaId}/reenviar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ motivo: motivo || undefined }),
  });
}

export function iniciarSessaoAdmin() {
  return apiFetch<{
    ok: true;
    token: string;
    user: { id: string; email: string; nome: string; role: string };
  }>("/api/admin/sessao/iniciar", {
    method: "POST",
    ehAdmin: true, // garante credentials: "include" — cookie CF Access viaja
  });
}

// ============================================================================
// Trocas de Prefixo IP
// ============================================================================

export type StatusTrocaPrefixo =
  | "rascunho"
  | "agendada"
  | "enviada"
  | "em_comunicacao"
  | "confirmada"
  | "executada"
  | "prazo_vencido"
  | "cancelada";

export interface TrocaPrefixoResumo {
  id: string;
  cliente_id: string;
  cliente_nome: string | null;
  prefixo_antigo: string;
  prefixo_novo: string;
  prazo_adequacao: string;
  status: StatusTrocaPrefixo;
  operador_email: string;
  enviada_em: string | null;
  confirmada_em: string | null;
  executada_em: string | null;
  criado_em: string;
  carta_id: string | null;
}

export interface TrocaPrefixoDetalhe {
  id: string;
  cliente_id: string;
  prefixo_antigo: string;
  prefixo_novo: string;
  prefixo_antigo_subnet_id: string | null;
  prefixo_novo_subnet_id: string | null;
  prefixo_novo_phpipam_subnet_id: string | null;
  prazo_adequacao: string;
  status: StatusTrocaPrefixo;
  motivo_troca: string | null;
  operador_id: string | null;
  operador_email: string;
  carta_id: string | null;
  enviada_em: string | null;
  confirmada_em: string | null;
  executada_em: string | null;
  cancelada_em: string | null;
  executada_metodo: string | null;
  criado_em: string;
  atualizado_em: string;
}

export interface MensagemTrocaPrefixo {
  id: string;
  troca_id: string;
  autor_tipo: "admin" | "cliente" | "sistema";
  autor_admin_id: string | null;
  autor_admin_email: string | null;
  autor_cliente_email: string | null;
  texto: string;
  evento_sistema: string | null;
  criado_em: string;
}

export interface CartaTroca {
  id: string;
  assunto: string;
  corpo: string;
  status: string;
  modo_envio: string;
  enviada_em: string | null;
  enviada_para_json: string | null;
  erro_envio: string | null;
}

export interface ListaTrocasResposta {
  ok: true;
  trocas: TrocaPrefixoResumo[];
  qtd_total: number;
  por_status: Array<{ status: StatusTrocaPrefixo; qtd: number }>;
  pagina: number;
  limit: number;
  total_paginas: number;
}

export interface FiltrosTrocas {
  pagina?: number;
  limit?: number;
  /** `em_andamento` = enviada, em comunicação, acuse recebido e prazo vencido (o cartão "Em andamento"). */
  status?: StatusTrocaPrefixo | "em_andamento" | "";
  cliente_id?: string;
  operador_id?: string;
  /** Só vale para troca EM ABERTO (executada e cancelada não têm prazo a cumprir). */
  prazo_filtro?: "vencidos" | "proximos_7d" | "futuros" | "";
  ordenar?: "prazo_adequacao" | "criado_em" | "cliente_nome" | "prefixo_antigo" | "status" | "operador_email";
  dir?: "asc" | "desc";
  q?: string;
  de?: string;
  ate?: string;
}

export function listarTrocasPrefixo(f: FiltrosTrocas = {}) {
  const p = new URLSearchParams();
  if (f.pagina) p.set("pagina", String(f.pagina));
  if (f.limit) p.set("limit", String(f.limit));
  if (f.status) p.set("status", f.status);
  if (f.cliente_id) p.set("cliente_id", f.cliente_id);
  if (f.operador_id) p.set("operador_id", f.operador_id);
  if (f.prazo_filtro) p.set("prazo_filtro", f.prazo_filtro);
  if (f.ordenar) p.set("ordenar", f.ordenar);
  if (f.dir) p.set("dir", f.dir);
  if (f.q) p.set("q", f.q);
  if (f.de) p.set("de", f.de);
  if (f.ate) p.set("ate", f.ate);
  return apiFetch<ListaTrocasResposta>(
    `/api/admin/trocas-prefixo${p.toString() ? `?${p}` : ""}`,
    { ehAdmin: true },
  );
}

export function obterTrocaPrefixo(id: string) {
  return apiFetch<{
    ok: true;
    troca: TrocaPrefixoDetalhe;
    cliente: { id: string; nome: string; razao_social: string | null; documento: string | null } | null;
    mensagens: MensagemTrocaPrefixo[];
    carta: CartaTroca | null;
  }>(`/api/admin/trocas-prefixo/${id}`, { ehAdmin: true });
}

export interface PayloadCriarTroca {
  cliente_id: string;
  /** FK clientes_subnets ATIVA (vigência aberta) — origem do prefixo antigo */
  prefixo_antigo_subnet_id: string;
  /** ID da subnet escolhida no phpIPAM (livre = sem customer_id) */
  prefixo_novo_phpipam_subnet_id: string;
  prazo_adequacao: string;
  motivo_troca?: string | null;
}

export function criarTrocaPrefixo(payload: PayloadCriarTroca) {
  return apiFetch<{ ok: true; id: string }>("/api/admin/trocas-prefixo", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

export interface PayloadEditarTroca {
  prazo_adequacao?: string;
  motivo_troca?: string | null;
}

export function editarTrocaPrefixo(id: string, payload: PayloadEditarTroca) {
  return apiFetch<{ ok: true }>(`/api/admin/trocas-prefixo/${id}`, {
    method: "PUT",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

/** Lista subnets ATIVAS do cliente (origem do "prefixo antigo" no form) */
export interface SubnetClienteAtiva {
  id: string;
  cidr: string;
  observacao: string | null;
  phpipam_subnet_id: string | null;
  vigencia_fim: string | null;
}
export function listarSubnetsCliente(clienteId: string) {
  return apiFetch<{ ok: true; subnets: SubnetClienteAtiva[] }>(
    `/api/admin/trocas-prefixo/_/subnets-cliente/${clienteId}`,
    { ehAdmin: true },
  );
}

/** Busca subnets LIVRES no phpIPAM (origem do "prefixo novo" no form).
 *  Quando cliente_id é informado, também inclui subnets já designadas a
 *  esse cliente (caso típico: NOC reservou /29 no phpIPAM com customer_id
 *  correto antes de criar a troca). Cada item traz `ja_designada` pra UI
 *  mostrar badge. */
export interface SubnetPhpipamLivre {
  phpipam_subnet_id: string;
  cidr: string;
  description: string | null;
  ja_designada: boolean;
}
export function buscarSubnetsLivres(q: string, clienteId?: string) {
  const p = new URLSearchParams({ q });
  if (clienteId) p.set("cliente_id", clienteId);
  return apiFetch<{ ok: true; subnets: SubnetPhpipamLivre[]; aviso?: string }>(
    `/api/admin/trocas-prefixo/_/subnets-livres?${p}`,
    { ehAdmin: true },
  );
}

export function enviarTrocaPrefixo(id: string) {
  return apiFetch<{
    ok: true;
    destinatarios: string[];
    cc: string[];
    carta_id: string;
  }>(`/api/admin/trocas-prefixo/${id}/enviar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({}),
  });
}

export function confirmarTrocaPrefixo(id: string, observacao?: string) {
  return apiFetch<{ ok: true }>(`/api/admin/trocas-prefixo/${id}/confirmar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ observacao }),
  });
}

export function executarTrocaPrefixo(id: string, observacao?: string) {
  return apiFetch<{ ok: true }>(`/api/admin/trocas-prefixo/${id}/executar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ observacao }),
  });
}

export function cancelarTrocaPrefixo(id: string, motivo?: string) {
  return apiFetch<{ ok: true }>(`/api/admin/trocas-prefixo/${id}/cancelar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ motivo }),
  });
}

export function adicionarMensagemTroca(id: string, texto: string) {
  return apiFetch<{ ok: true }>(`/api/admin/trocas-prefixo/${id}/mensagens`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ texto }),
  });
}

export function listarTrocasProximas() {
  return apiFetch<{
    ok: true;
    lista: TrocaPrefixoResumo[];
    agora: string;
  }>("/api/admin/trocas-prefixo/_/proximas", { ehAdmin: true });
}

export interface OficioProximo {
  id: string;
  numero_oficio: string;
  requester: string;
  ip_publico_alvo: string | null;
  prazo_resposta: string;
  status: "recebido" | "em_analise" | "aguardando_info";
  responsavel_email: string | null;
}
export function listarOficiosProximos() {
  return apiFetch<{
    ok: true;
    lista: OficioProximo[];
    agora: string;
  }>("/api/admin/oficios/_/proximos", { ehAdmin: true });
}

// ============================================================================
// Forense CGNAT — console técnico + ofícios judiciais
// ============================================================================

export interface ForenseSaude {
  status: string;
  time_utc: string;
  clickhouse: string;
}
export interface ForenseConcentrador {
  code: string;
  name: string;
  subscriber_source: "none" | "ixc";
  client_system: string;
}

export function forenseSaude() {
  return apiFetch<{ ok: boolean; data: ForenseSaude | null; duracao_ms: number; erro?: string }>(
    "/api/admin/forense/saude",
    { ehAdmin: true },
  );
}

export function forenseConcentradores() {
  return apiFetch<{
    ok: true;
    cached: boolean;
    concentrators: ForenseConcentrador[];
  }>("/api/admin/forense/concentradores", { ehAdmin: true });
}

export interface EventoCgnat {
  event_time_utc?: string;
  public_ip?: string;
  public_port?: number;
  protocol?: string;
  private_ip?: string;
  private_port?: number;
  destination_ip?: string;
  destination_port?: number;
  raw_log?: string;
  login_resolvido?: string;
  mac_resolvido?: string;
  id_cliente?: number;
  cliente_nome?: string;
  cliente_documento?: string;
  email?: string;
  cidade?: string;
  bairro?: string;
  endereco?: string;
  concentrator_code?: string;
  concentrator_name?: string;
  a10_source_ip?: string;
  [k: string]: unknown;
}

export interface RespostaBuscaForense {
  search_id: string;
  searched_at_utc: string;
  oficio?: string;
  requester?: string;
  criteria?: Record<string, unknown>;
  result_count: number;
  results: EventoCgnat[];
  sha256: string;
}

interface RetornoBusca {
  ok: boolean;
  data: RespostaBuscaForense | null;
  erro?: string;
  status: number;
}

// Console técnico
export const forenseBuscaPublica = (p: {
  concentrator_code: string;
  public_ip: string;
  start_time_utc: string;
  end_time_utc: string;
  protocol?: string;
  public_port?: number;
  destination_ip?: string;
  destination_port?: number;
  include_raw_log?: boolean;
  limit?: number;
  forcar_dia_atual?: boolean;
}) =>
  apiFetch<RetornoBusca>("/api/admin/forense/buscas/publica", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(p),
  });

export const forenseBuscaDestino = (p: {
  concentrator_code: string;
  destination_ip: string;
  destination_port?: number;
  start_time_utc: string;
  end_time_utc: string;
  protocol?: string;
  limit?: number;
  forcar_dia_atual?: boolean;
}) =>
  apiFetch<RetornoBusca>("/api/admin/forense/buscas/destino", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(p),
  });

export const forenseBuscaPrivada = (p: {
  concentrator_code: string;
  private_ip: string;
  start_time_utc: string;
  end_time_utc: string;
  protocol?: string;
  limit?: number;
}) =>
  apiFetch<RetornoBusca>("/api/admin/forense/buscas/privada", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(p),
  });

export const forenseBuscaMac = (p: {
  concentrator_code: string;
  mac: string;
  start_time_utc: string;
  end_time_utc: string;
  protocol?: string;
  limit?: number;
}) =>
  apiFetch<RetornoBusca>("/api/admin/forense/buscas/mac", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(p),
  });

export const forenseBuscaAssinante = (p: {
  concentrator_code: string;
  id_cliente?: number;
  id_contrato?: number;
  login?: string;
  document?: string;
  name?: string;
  private_ip?: string;
  mac?: string;
  limit?: number;
}) =>
  apiFetch<{ ok: boolean; data: unknown; erro?: string; status: number }>(
    "/api/admin/forense/buscas/assinante",
    { method: "POST", ehAdmin: true, body: JSON.stringify(p) },
  );

export const forenseBuscaRadius = (p: {
  concentrator_code: string;
  login?: string;
  id_cliente?: number;
  start_time_utc: string;
  end_time_utc: string;
  limit?: number;
}) =>
  apiFetch<{ ok: boolean; data: unknown; erro?: string; status: number }>(
    "/api/admin/forense/buscas/radius",
    { method: "POST", ehAdmin: true, body: JSON.stringify(p) },
  );

export const forenseBuscaClienteAcessos = (p: {
  concentrator_code: string;
  id_cliente: number;
  start_time_utc: string;
  end_time_utc: string;
  protocol?: string;
  include_raw_log?: boolean;
  limit?: number;
  forcar_dia_atual?: boolean;
}) =>
  apiFetch<RetornoBusca>("/api/admin/forense/buscas/cliente-acessos", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(p),
  });

export const forenseBuscaClienteResumo = (p: {
  concentrator_code: string;
  id_cliente: number;
  start_time_utc: string;
  end_time_utc: string;
  group_by: "destination" | "destination_port" | "public_ip" | "protocol" | "private_ip";
  top_n?: number;
}) =>
  apiFetch<{ ok: boolean; data: unknown; erro?: string; status: number }>(
    "/api/admin/forense/buscas/cliente-resumo",
    { method: "POST", ehAdmin: true, body: JSON.stringify(p) },
  );

export const forenseBuscaNatResumo = (p: {
  concentrator_code: string;
  start_time_utc: string;
  end_time_utc: string;
  group_by: "public_ip" | "destination" | "destination_port" | "protocol" | "client" | "a10_source";
  top_n?: number;
  // Filtros pra drill-down
  a10_source_ip?: string;
  public_ip?: string;
  destination_ip?: string;
  destination_port?: number;
  protocol?: string;
  id_cliente?: number;
  device_name?: string;
  a10_host?: string;
  partition_name?: string;
  forcar_dia_atual?: boolean;
}) =>
  apiFetch<{ ok: boolean; data: unknown; erro?: string; status: number }>(
    "/api/admin/forense/buscas/nat-resumo",
    { method: "POST", ehAdmin: true, body: JSON.stringify(p) },
  );

export interface InfoIpAsn {
  ip: string;
  consultado_em: string;
  asns: number[];
  prefixo: string | null;
  holder: string | null;
  pais: string | null;
  indisponivel: boolean;
  motivo?: string;
}
export const forenseAsnBatch = (ips: string[]) =>
  apiFetch<{ ok: true; resultados: Record<string, InfoIpAsn> }>(
    "/api/admin/forense/asn-batch",
    { method: "POST", ehAdmin: true, body: JSON.stringify({ ips }) },
  );

export interface ForenseHistoricoItem {
  id: string;
  tipo: string;
  concentrator_code: string | null;
  endpoint: string;
  payload_json: string;
  resposta_resumo_json: string | null;
  search_id: string | null;
  sha256: string | null;
  result_count: number | null;
  http_status: number | null;
  erro: string | null;
  criado_por_email: string;
  criado_em: string;
}
export interface RespostaForenseHistorico {
  ok: true;
  historico: ForenseHistoricoItem[];
  /** Total do MESMO filtro (COUNT). Antes a rota devolvia só as 200 mais recentes, sem total. */
  total: number;
  pagina: number;
  limit: number;
  total_paginas: number;
  /** Opções dos filtros: do banco inteiro, não das linhas da página. */
  tipos: string[];
  concentradores: string[];
}
export function forenseHistorico(
  params: {
    tipo?: string;
    concentrador?: string;
    q?: string;
    de?: string;
    ate?: string;
    ordenar?: "criado_em" | "criado_por_email" | "tipo" | "concentrator_code" | "result_count" | "search_id" | "sha256";
    dir?: "asc" | "desc";
    pagina?: number;
    limit?: number;
  } = {},
) {
  const q = new URLSearchParams();
  if (params.tipo) q.set("tipo", params.tipo);
  if (params.concentrador) q.set("concentrador", params.concentrador);
  if (params.q) q.set("q", params.q);
  if (params.de) q.set("de", params.de);
  if (params.ate) q.set("ate", params.ate);
  if (params.ordenar) q.set("ordenar", params.ordenar);
  if (params.dir) q.set("dir", params.dir);
  if (params.pagina && params.pagina > 1) q.set("pagina", String(params.pagina));
  if (params.limit) q.set("limit", String(params.limit));
  return apiFetch<RespostaForenseHistorico>(`/api/admin/forense/historico${q.toString() ? `?${q}` : ""}`, { ehAdmin: true });
}

// Ofícios
export type StatusOficio =
  | "recebido"
  | "em_analise"
  | "aguardando_info"
  | "respondido"
  | "arquivado"
  | "cancelado";

export interface OficioJudicial {
  id: string;
  numero_oficio: string;
  requester: string;
  requester_email: string | null;
  procedimento: string | null;
  observacoes: string | null;
  ip_publico_alvo: string | null;
  porta_publica_alvo: number | null;
  protocolo_alvo: string | null;
  evento_em_utc: string | null;
  janela_segundos: number | null;
  concentrator_code: string | null;
  status: StatusOficio;
  prazo_resposta: string | null;
  recebido_em: string;
  respondido_em: string | null;
  arquivado_em: string | null;
  numero_protocolo_resposta: string | null;
  respondido_via: "email" | "sistema" | "presencial" | "outro" | null;
  respondido_para: string | null;
  observacao_resposta: string | null;
  alerta_prazo_em: string | null;
  responsavel_id: string | null;
  responsavel_email: string | null;
  criado_por_email: string;
  criado_em: string;
  atualizado_em: string;
}

export interface OficioBusca {
  id: string;
  oficio_id: string;
  tipo: "conclusiva" | "sem_porta" | "tecnica_complementar";
  endpoint: string;
  payload_json: string;
  resposta_resumo_json: string | null;
  search_id: string | null;
  searched_at_utc: string | null;
  sha256: string | null;
  result_count: number | null;
  http_status: number | null;
  erro: string | null;
  criado_por_email: string;
  criado_em: string;
}

export interface OficioExportRegistro {
  id: string;
  oficio_id: string;
  busca_id: string;
  export_id: string;
  search_id: string;
  manifest_sha256: string;
  files_json: string;
  criado_por_email: string;
  criado_em: string;
}

export type OrdemOficio = "recebido_em" | "criado_em" | "numero_oficio" | "requester" | "status" | "prazo_resposta";
export function listarOficios(params: {
  pagina?: number;
  limit?: number;
  status?: StatusOficio | "";
  q?: string;
  de?: string;
  ate?: string;
  /** Ofício EM ABERTO com prazo vencido (`vencido`) ou vencido/a vencer em 7 dias (`7d`). */
  prazo?: "vencido" | "7d" | "";
  ordenar?: OrdemOficio;
  dir?: "asc" | "desc";
} = {}) {
  const qs = new URLSearchParams();
  if (params.pagina) qs.set("pagina", String(params.pagina));
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.status) qs.set("status", params.status);
  if (params.q) qs.set("q", params.q);
  if (params.de) qs.set("de", params.de);
  if (params.ate) qs.set("ate", params.ate);
  if (params.prazo) qs.set("prazo", params.prazo);
  if (params.ordenar) qs.set("ordenar", params.ordenar);
  if (params.dir) qs.set("dir", params.dir);
  return apiFetch<{
    ok: true;
    oficios: OficioJudicial[];
    qtd_total: number;
    pagina: number;
    limit: number;
    total_paginas: number;
    /** Conta com os mesmos filtros da lista, menos o de status. */
    por_status: Array<{ status: StatusOficio; qtd: number }>;
    /** Em aberto e com prazo vencido, com busca/período/responsável, sem status nem prazo. */
    qtd_prazo_vencido: number;
  }>(`/api/admin/oficios${qs.toString() ? `?${qs}` : ""}`, { ehAdmin: true });
}

export function obterOficio(id: string) {
  return apiFetch<{
    ok: true;
    oficio: OficioJudicial;
    buscas: OficioBusca[];
    exports: OficioExportRegistro[];
  }>(`/api/admin/oficios/${id}`, { ehAdmin: true });
}

export interface PayloadCriarOficio {
  numero_oficio: string;
  requester: string;
  requester_email?: string;
  procedimento?: string;
  observacoes?: string;
  ip_publico_alvo?: string;
  porta_publica_alvo?: number;
  protocolo_alvo?: "TCP" | "UDP" | "ANY";
  evento_em_utc?: string;
  janela_segundos?: number;
  concentrator_code?: string;
  prazo_resposta?: string;
  recebido_em?: string;
}
export function criarOficio(p: PayloadCriarOficio) {
  return apiFetch<{ ok: true; id: string }>("/api/admin/oficios", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(p),
  });
}
export function editarOficio(id: string, p: Partial<PayloadCriarOficio>) {
  return apiFetch<{ ok: true }>(`/api/admin/oficios/${id}`, {
    method: "PUT",
    ehAdmin: true,
    body: JSON.stringify(p),
  });
}

export function oficioBuscarConclusiva(id: string, p: {
  concentrator_code: string;
  public_ip: string;
  public_port: number;
  protocol: "TCP" | "UDP";
  event_time_utc: string;
  window_seconds?: number;
  limit?: number;
}) {
  return apiFetch<RetornoBusca & { busca_id: string }>(
    `/api/admin/oficios/${id}/buscar/conclusiva`,
    { method: "POST", ehAdmin: true, body: JSON.stringify(p) },
  );
}
export function oficioBuscarSemPorta(id: string, p: {
  concentrator_code: string;
  public_ip: string;
  protocol: "TCP" | "UDP" | "ANY";
  event_time_utc: string;
  window_seconds?: number;
  limit?: number;
}) {
  return apiFetch<RetornoBusca & { busca_id: string }>(
    `/api/admin/oficios/${id}/buscar/sem-porta`,
    { method: "POST", ehAdmin: true, body: JSON.stringify(p) },
  );
}
export function oficioExportar(oficioId: string, buscaId: string) {
  return apiFetch<{
    ok: true;
    registro_id: string;
    export_id: string;
    search_id: string;
    files: string[];
    manifest_sha256: string;
  }>(`/api/admin/oficios/${oficioId}/exportar/${buscaId}`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({}),
  });
}
export function oficioArquivoUrl(oficioId: string, buscaId: string, nome: string): string {
  return `${BASE}/api/admin/oficios/${oficioId}/arquivos/${buscaId}/${encodeURIComponent(nome)}`;
}
export interface RespostaOficioPayload {
  numero_protocolo_resposta?: string;
  respondido_via?: "email" | "sistema" | "presencial" | "outro";
  respondido_para?: string;
  observacao_resposta?: string;
}
export function oficioResponder(id: string, payload: RespostaOficioPayload = {}) {
  return apiFetch<{ ok: true }>(`/api/admin/oficios/${id}/responder`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}
export function oficioArquivar(id: string) {
  return apiFetch<{ ok: true }>(`/api/admin/oficios/${id}/arquivar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({}),
  });
}
export function oficioCancelar(id: string, observacao?: string) {
  return apiFetch<{ ok: true }>(`/api/admin/oficios/${id}/cancelar`, {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({ observacao }),
  });
}

// ─── Infra: logs de equipamentos ──────────────────────────────────────
export interface InfraDevice {
  device_id: string;
  name: string;
  source_ip: string;
  role: string;
  vendor?: string | null;
  model?: string | null;
  site?: string | null;
  pop?: string | null;
  parser?: string | null;
  enabled?: 0 | 1 | boolean;
  retention_policy?: string | null;
  notes?: string | null;
  updated_at?: string | null;
}

export interface InfraDeviceFiltros {
  role?: string;
  vendor?: string;
  site?: string;
  pop?: string;
  enabled?: "0" | "1";
  q?: string;
  limit?: number;
  offset?: number;
}

export interface InfraDeviceCreate {
  device_id: string;
  name: string;
  source_ip: string;
  role: string;
  vendor?: string;
  model?: string;
  site?: string;
  pop?: string;
  parser?: string;
  enabled?: boolean;
  retention_policy?: string;
  notes?: string;
}

export type InfraDevicePatch = Partial<Omit<InfraDeviceCreate, "device_id">>;

export function infraRoles() {
  return apiFetch<{ ok: true; roles: string[]; fonte: "cache" | "upstream" }>(
    "/api/admin/infra/device-roles",
    { ehAdmin: true },
  );
}

export function infraEventTypes() {
  return apiFetch<{
    ok: true;
    event_types: string[];
    fonte: "cache" | "upstream";
  }>("/api/admin/infra/event-types", { ehAdmin: true });
}

export function infraListarDevices(filtros: InfraDeviceFiltros = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<{ ok: true; devices: InfraDevice[] }>(
    `/api/admin/infra/devices${suffix}`,
    { ehAdmin: true },
  );
}

export function infraObterDevice(deviceId: string) {
  return apiFetch<{ ok: true; device: InfraDevice }>(
    `/api/admin/infra/devices/${encodeURIComponent(deviceId)}`,
    { ehAdmin: true },
  );
}

export function infraCriarDevice(payload: InfraDeviceCreate) {
  return apiFetch<{ ok: true; device: InfraDevice }>(
    "/api/admin/infra/devices",
    { method: "POST", ehAdmin: true, body: JSON.stringify(payload) },
  );
}

export function infraAtualizarDevice(deviceId: string, payload: InfraDevicePatch) {
  return apiFetch<{ ok: true; device: InfraDevice }>(
    `/api/admin/infra/devices/${encodeURIComponent(deviceId)}`,
    { method: "PATCH", ehAdmin: true, body: JSON.stringify(payload) },
  );
}

export function infraInvalidarCache() {
  return apiFetch<{ ok: true }>("/api/admin/infra/_/cache/invalidar", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify({}),
  });
}

// ─── Infra: catálogo (marcas + modelos) ───────────────────────────────
export interface InfraMarca {
  id: string;
  nome: string;
  slug: string;
  ativo: number;
  observacoes: string | null;
}

export interface InfraModelo {
  id: string;
  marca_id: string;
  nome: string;
  slug: string;
  parser_default: string | null;
  tipo_sugerido: string | null;
  retention_default: string | null;
  observacoes: string | null;
  ativo: number;
}

export function infraListarMarcas() {
  return apiFetch<{ ok: true; marcas: InfraMarca[] }>(
    "/api/admin/infra/catalogo/marcas",
    { ehAdmin: true },
  );
}

export function infraCriarMarca(payload: { nome: string; observacoes?: string }) {
  return apiFetch<{ ok: true; marca_id: string }>(
    "/api/admin/infra/catalogo/marcas",
    { method: "POST", ehAdmin: true, body: JSON.stringify(payload) },
  );
}

export function infraAtualizarMarca(
  id: string,
  payload: { nome?: string; ativo?: boolean; observacoes?: string },
) {
  return apiFetch<{ ok: true }>(`/api/admin/infra/catalogo/marcas/${id}`, {
    method: "PATCH",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

export function infraListarModelos(marca_id?: string) {
  const suffix = marca_id ? `?marca_id=${encodeURIComponent(marca_id)}` : "";
  return apiFetch<{ ok: true; modelos: InfraModelo[] }>(
    `/api/admin/infra/catalogo/modelos${suffix}`,
    { ehAdmin: true },
  );
}

export function infraCriarModelo(payload: {
  marca_id: string;
  nome: string;
  parser_default?: string;
  tipo_sugerido?: string;
  retention_default?: string;
  observacoes?: string;
}) {
  return apiFetch<{ ok: true; modelo_id: string }>(
    "/api/admin/infra/catalogo/modelos",
    { method: "POST", ehAdmin: true, body: JSON.stringify(payload) },
  );
}

export function infraAtualizarModelo(
  id: string,
  payload: {
    nome?: string;
    parser_default?: string;
    tipo_sugerido?: string;
    retention_default?: string;
    observacoes?: string;
    ativo?: boolean;
  },
) {
  return apiFetch<{ ok: true }>(`/api/admin/infra/catalogo/modelos/${id}`, {
    method: "PATCH",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

// ─── Infra: phpIPAM lookup ────────────────────────────────────────────
export interface InfraPhpipamLookup {
  ok: true;
  encontrado: boolean;
  info?: {
    address_id: string | null;
    subnet_id: string | null;
    subnet_cidr: string | null;
    description: string | null;
    hostname: string | null;
    customer_id: string | number | null;
    customer_nome: string | null;
    origem: "address" | "subnet";
  };
}

export function infraPhpipamLookup(ip: string) {
  return apiFetch<InfraPhpipamLookup>(
    `/api/admin/infra/phpipam/ip/${encodeURIComponent(ip)}`,
    { ehAdmin: true },
  );
}

export function infraPhpipamCriarAddress(payload: {
  ip: string;
  subnet_id: string;
  description?: string;
  hostname?: string;
}) {
  return apiFetch<{ ok: true; address_id: string | null }>(
    "/api/admin/infra/phpipam/ip",
    { method: "POST", ehAdmin: true, body: JSON.stringify(payload) },
  );
}

export interface InfraPhpipamLocation {
  id: number | string;
  name: string;
  description?: string | null;
  address?: string | null;
}

export function infraPhpipamLocations() {
  return apiFetch<{ ok: true; locations: InfraPhpipamLocation[] }>(
    "/api/admin/infra/phpipam/locations",
    { ehAdmin: true },
  );
}

// ─── Infra: Device extras (metadata local) ────────────────────────────
export interface InfraDeviceMeta {
  device_id: string;
  marca_id: string | null;
  modelo_id: string | null;
  phpipam_address_id: number | null;
}

export interface InfraDeviceCreatePlus extends InfraDeviceCreate {
  marca_id?: string;
  modelo_id?: string;
  phpipam_address_id?: number;
  confirmar_conflito_cliente?: boolean;
}

export interface InfraDevicePatchPlus extends InfraDevicePatch {
  marca_id?: string | null;
  modelo_id?: string | null;
  phpipam_address_id?: number | null;
}

export function infraObterDeviceComMeta(deviceId: string) {
  return apiFetch<{ ok: true; device: InfraDevice; meta: InfraDeviceMeta | null }>(
    `/api/admin/infra/devices/${encodeURIComponent(deviceId)}`,
    { ehAdmin: true },
  );
}

export interface InfraConflitoIp {
  ok: false;
  erro: "ip_vinculado_a_cliente";
  customer_id: string | number;
  customer_nome: string | null;
  mensagem: string;
}

// ─── Infra: Busca de logs ─────────────────────────────────────────────
export interface InfraLogItem {
  event_time_utc: string;
  received_time_utc: string;
  device_id: string;
  device_name: string;
  device_role: string;
  vendor: string;
  model: string;
  source_ip: string;
  site: string;
  pop: string;
  severity: string;
  event_type: string;
  interface: string | null;
  vlan: number | null;
  mac: string | null;
  onu_id: string | null;
  pon_port: string | null;
  slot: string | null;
  port: string | null;
  peer_ip: string | null;
  protocol: string | null;
  username: string | null;
  message: string;
  raw_log?: string;
  parser_version: string;
  // Campos de auditoria/roteador — preenchidos conforme o parser (Huawei
  // VRP, Datacom DMOS, IOS-XE). O /logs/search genérico também os devolve.
  event_family?: string | null;
  event_code?: string | null;
  access_ip?: string | null;
  auth_method?: string | null;
  auth_result?: string | null;
  command?: string | null;
  error_code?: string | null;
  disconnect_cause?: string | null;
}

export interface InfraLogSearchPayload {
  start_time_utc: string;
  end_time_utc: string;
  device_id?: string;
  source_ip?: string;
  role?: string;
  vendor?: string;
  site?: string;
  pop?: string;
  severity?: string;
  event_type?: string;
  interface?: string;
  mac?: string;
  onu_id?: string;
  pon_port?: string;
  text?: string;
  include_raw_log?: boolean;
  limit?: number;
  offset?: number;
}

export function infraBuscarLogs(payload: InfraLogSearchPayload) {
  return apiFetch<{
    ok: true;
    criteria: InfraLogSearchPayload;
    results: InfraLogItem[];
  }>("/api/admin/infra/logs/search", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

// ─── Infra: Dashboard ─────────────────────────────────────────────────
export interface InfraSummaryItem {
  key: string;
  value: number;
}
export interface InfraUnknownSource {
  source_ip: string;
  events: number;
  last_event_utc: string;
}
/**
 * Janela que a API usou nas rotas de KPI de Infra. `last_hours` é null quando o período veio do calendário (`de`/`ate`, dia da
 * Bahia). `conferida`: só em calendário; "sem_dados" = dia sem eventos (nada a conferir).
 */
export interface JanelaInfraResposta {
  start_utc: string;
  end_utc: string;
  last_hours: number | null;
  de?: string | null;
  ate?: string | null;
  conferida?: "confirmada" | "sem_dados";
}

/** Período de uma consulta de KPI: horas OU dias do calendário (`lib/periodo-infra.ts`). */
export interface ConsultaPeriodo {
  last_hours?: number;
  de?: string;
  ate?: string;
}

function qsDoPeriodo(p: ConsultaPeriodo, padraoHoras: number): string {
  return p.de && p.ate ? `de=${p.de}&ate=${p.ate}` : `last_hours=${p.last_hours ?? padraoHoras}`;
}

export interface InfraDashboard {
  ok: true;
  janela: JanelaInfraResposta;
  totais: {
    events: number;
    devices: number;
    first_event_utc?: string;
    last_event_utc?: string;
  } | null;
  by_role: InfraSummaryItem[];
  by_event_type: InfraSummaryItem[];
  by_severity: InfraSummaryItem[];
  unknown_sources: InfraUnknownSource[];
  seguranca: {
    ultimos_logins: InfraLogItem[];
    qtd_logins: number;
    qtd_falhas: number;
    ultimas_falhas: InfraLogItem[];
  };
  config: {
    ultimas_mudancas: InfraLogItem[];
    qtd_mudancas: number;
  };
  saude: {
    qtd_alarmes: number;
    ultimos_alarmes: InfraLogItem[];
  };
  rede: {
    qtd_link_down: number;
    qtd_bgp_down: number;
    ultimos_link_down: InfraLogItem[];
    ultimos_bgp_down: InfraLogItem[];
  };
  silenciosos: InfraDevice[];
}

export function infraDashboard(periodo: ConsultaPeriodo | number = 24) {
  const p = typeof periodo === "number" ? { last_hours: periodo } : periodo;
  return apiFetch<InfraDashboard>(
    `/api/admin/infra/dashboard?${qsDoPeriodo(p, 24)}`,
    { ehAdmin: true },
  );
}

// ─── Infra: Origens desconhecidas ─────────────────────────────────────
export function infraUnknownSources(periodo: ConsultaPeriodo | number = 24) {
  const p = typeof periodo === "number" ? { last_hours: periodo } : periodo;
  return apiFetch<{
    ok: true;
    janela_horas: number | null;
    janela?: JanelaInfraResposta;
    sources: InfraUnknownSource[];
  }>(`/api/admin/infra/unknown-sources?${qsDoPeriodo(p, 24)}`, {
    ehAdmin: true,
  });
}

// ─── Infra: Firewall ASA ──────────────────────────────────────────────
export interface FirewallLogItem extends InfraLogItem {
  asa_severity: number | null;
  asa_event_code: number | null;
  action: string | null;
  src_interface: string | null;
  src_ip: string | null;
  src_port: number | null;
  dst_interface: string | null;
  dst_ip: string | null;
  dst_port: number | null;
}

export interface FirewallSearchPayload {
  device_id: string;
  last_hours?: number;
  start_time_utc?: string;
  end_time_utc?: string;
  src_ip?: string;
  dst_ip?: string;
  src_port?: number;
  dst_port?: number;
  src_interface?: string;
  dst_interface?: string;
  action?: string;
  asa_event_code?: number;
  portas_sensiveis?: boolean;
  apenas_para_ip_publico?: boolean;
  text?: string;
  include_raw_log?: boolean;
  limit?: number;
}

export interface FirewallDashboard {
  ok: true;
  janela: JanelaInfraResposta;
  device: InfraDevice | null;
  ip_publico: string | null;
  totais: {
    eventos: number;
    descartados: number;
    embryonic: number;
    criticos: number;
    portas_sensiveis: number;
    tentativas_ip_publico: number;
    sip: number;
    dmz: number;
  };
  top_src_ips: Array<{ key: string; count: number }>;
  top_dst_ports: Array<{ key: number; count: number }>;
  top_event_codes: Array<{ key: number; count: number }>;
  timeline: Array<{ hour: string; total: number; denied: number; allowed: number }>;
}

export function firewallListarDevices() {
  return apiFetch<{ ok: true; devices: InfraDevice[] }>(
    "/api/admin/infra/firewall/devices",
    { ehAdmin: true },
  );
}

export function firewallBuscarLogs(payload: FirewallSearchPayload) {
  return apiFetch<{
    ok: true;
    janela: { start_utc: string; end_utc: string };
    total: number;
    pre_parse: number;
    results: FirewallLogItem[];
  }>("/api/admin/infra/firewall/logs/search", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

export function firewallDashboard(device_id: string, periodo: ConsultaPeriodo | number = 72) {
  const p = typeof periodo === "number" ? { last_hours: periodo } : periodo;
  return apiFetch<FirewallDashboard>(
    `/api/admin/infra/firewall/dashboard?device_id=${encodeURIComponent(device_id)}&${qsDoPeriodo(p, 72)}`,
    { ehAdmin: true },
  );
}

// ─── Infra: Auditoria de Switches (Huawei VRP + L3) ──────────────────
export interface AuditLogItem extends InfraLogItem {
  access_ip?: string | null;
  vpn_name?: string | null;
  auth_method?: string | null;
  command?: string | null;
  event_family?: string | null;
  event_code?: string | null;
  config_change_number?: number | null;
}

export interface AuditSearchPayload {
  device_id?: string;
  username?: string;
  access_ip?: string;
  vpn_name?: string;
  auth_method?: string;
  event_family?: string;
  event_code?: string;
  command?: string;
  last_hours?: number;
  start_time_utc?: string;
  end_time_utc?: string;
  apenas_sensiveis?: boolean;
  text?: string;
  include_raw_log?: boolean;
  limit?: number;
}

export interface AuditDashboard {
  ok: true;
  janela: JanelaInfraResposta;
  totais: {
    eventos: number;
    comandos: number;
    saves: number;
    mudancas_config: number;
    comandos_sensiveis: number;
  };
  top_users: Array<{ key: string; count: number }>;
  top_access_ips: Array<{ key: string; count: number }>;
  top_changed_devices: Array<{ key: string; count: number }>;
  top_commands: Array<{ key: string; count: number }>;
  timeline: Array<{ hour: string; total: number }>;
}

export function auditListarDevices() {
  return apiFetch<{ ok: true; devices: InfraDevice[] }>(
    "/api/admin/infra/audit/devices",
    { ehAdmin: true },
  );
}

export function auditBuscarLogs(payload: AuditSearchPayload) {
  return apiFetch<{
    ok: true;
    janela: { start_utc: string; end_utc: string };
    total: number;
    results: AuditLogItem[];
  }>("/api/admin/infra/audit/logs/search", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

export function auditDashboard(opts: { device_id?: string; username?: string } & ConsultaPeriodo = {}) {
  const qs = new URLSearchParams();
  if (opts.device_id) qs.set("device_id", opts.device_id);
  if (opts.username) qs.set("username", opts.username);
  if (opts.de && opts.ate) {
    qs.set("de", opts.de);
    qs.set("ate", opts.ate);
  } else {
    qs.set("last_hours", String(opts.last_hours ?? 72));
  }
  return apiFetch<AuditDashboard>(
    `/api/admin/infra/audit/dashboard?${qs.toString()}`,
    { ehAdmin: true },
  );
}

// ─── Infra: Roteadores / BNG ──────────────────────────────────────────
// Visão operacional de roteadores e BNGs (Cisco IOS-XE e similares L3
// edge): adjacência de roteamento, link/line-protocol, sessões de
// assinante, audit e saúde de HW. Agregação interim server-side.
export interface RouterLogItem extends InfraLogItem {
  event_family?: string | null;
  event_code?: string | null;
  // Campos IOS-XE / BNG (parser cisco_ios_syslog, entregues 2026-06-09)
  cisco_mnemonic?: string | null;
  subscriber_username?: string | null;
  auth_method?: string | null;
  auth_result?: string | null;
  error_code?: string | null;
  disconnect_cause?: string | null;
}

export type RouterCategoria =
  | "roteamento"
  | "link"
  | "acesso"
  | "config"
  | "hardware"
  | "assinante";

export interface RouterSearchPayload {
  device_id?: string;
  categoria?: RouterCategoria;
  event_type?: string;
  event_family?: string;
  event_code?: string;
  username?: string;
  subscriber_username?: string;
  auth_result?: string;
  interface?: string;
  peer_ip?: string;
  severity?: string;
  text?: string;
  last_hours?: number;
  start_time_utc?: string;
  end_time_utc?: string;
  include_raw_log?: boolean;
  limit?: number;
}

export interface RouterOverview {
  ok: true;
  janela: { start_utc: string; end_utc: string; last_hours: number };
  /** Eventos amostrados p/ categoria/timeline. Se == 3000, está truncada. */
  amostra: number;
  totais: {
    eventos: number;
    roteamento: number;
    link: number;
    acesso: number;
    config: number;
    hardware: number;
    assinante: number;
    auth_ok: number;
    auth_fail: number;
  };
  top_interfaces: Array<{ key: string; count: number }>;
  top_peers: Array<{ key: string; count: number }>;
  top_error_codes: Array<{ key: string; count: number }>;
  top_auth_fail_users: Array<{ key: string; count: number }>;
  top_event_types: Array<{ key: string; count: number }>;
  timeline: Array<{ hour: string; total: number }>;
}

export function routerListarDevices() {
  return apiFetch<{ ok: true; devices: InfraDevice[] }>(
    "/api/admin/infra/router/devices",
    { ehAdmin: true },
  );
}

export function routerBuscarLogs(payload: RouterSearchPayload) {
  return apiFetch<{
    ok: true;
    janela: { start_utc: string; end_utc: string };
    total: number;
    results: RouterLogItem[];
  }>("/api/admin/infra/router/logs/search", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

export function routerOverview(opts: { device_id?: string; last_hours?: number } = {}) {
  const qs = new URLSearchParams();
  if (opts.device_id) qs.set("device_id", opts.device_id);
  qs.set("last_hours", String(opts.last_hours ?? 72));
  return apiFetch<RouterOverview>(
    `/api/admin/infra/router/overview?${qs.toString()}`,
    { ehAdmin: true },
  );
}

// Sessões PPPoE — fonte IXC radacct (RADIUS accounting), quase-tempo-real.
// Gate backend: forense.consultar_tecnico (master/seguranca_admin/noc).
export interface RouterSessao {
  session_id: string | null;
  username: string | null;
  nas_ip: string | null;
  nas_port: string | null;
  framed_ip: string | null;
  framed_ipv6: string | null;
  mac: string | null;
  inicio: string | null;
  fim: string | null;
  ativa: boolean;
  duracao_seg: number;
  upload_bytes: number;
  download_bytes: number;
  causa_termino: string | null;
}

export interface RouterSessoesPayload {
  username?: string;
  nas_ip?: string;
  framed_ip?: string;
  mac?: string;
  apenas_ativas?: boolean;
  page?: number;
  rp?: number;
}

/** Titular da conexão, resolvido no IXC quando a busca é por login. */
export interface RouterSessaoDono {
  login: string;
  online: boolean;
  id_cliente: string;
  cliente_ixc: {
    id: string;
    razao: string | null;
    fantasia: string | null;
    tipo_pessoa: string | null;
    cnpj_cpf: string | null;
    telefone: string | null;
    celular: string | null;
    cidade: string | null;
    uf: string | null;
    ativo: boolean;
  } | null;
  cliente_local: { id: string; nome: string } | null;
}

export function routerSessoes(payload: RouterSessoesPayload) {
  return apiFetch<{
    ok: true;
    fonte: string;
    total_upstream: number | null;
    page: number;
    rp: number;
    count: number;
    dono: RouterSessaoDono | null;
    sessoes: RouterSessao[];
  }>("/api/admin/infra/router/sessoes", {
    method: "POST",
    ehAdmin: true,
    body: JSON.stringify(payload),
  });
}

// Snapshot radusuarios do IXC — estado atual de cada login PPPoE.
// Usos: "online agora" (apenas_online) e autocomplete por login (q).
export interface RouterAssinante {
  id: string | null;
  login: string | null;
  online: boolean;
  ip: string | null;
  mac: string | null;
  conectado_desde: string | null;
  ultima_desconexao: string | null;
  id_cliente: string | null;
  /** Razão social do cliente no IXC — preenchido só na busca por nome. */
  cliente_ixc_razao: string | null;
  /** Cliente local do Sentinela vinculado via external_customer_id_ixc, se importado. */
  cliente: { id: string; nome: string } | null;
}

export function routerAssinantes(
  opts: {
    q?: string;
    /** Busca por nome do cliente (ou CPF/CNPJ) — agrega os logins de até 6 clientes. */
    nome?: string;
    apenas_online?: boolean;
    id_cliente?: string;
    rp?: number;
    page?: number;
  } = {},
) {
  const qs = new URLSearchParams();
  if (opts.q) qs.set("q", opts.q);
  if (opts.nome) qs.set("nome", opts.nome);
  if (opts.apenas_online) qs.set("apenas_online", "1");
  if (opts.id_cliente) qs.set("id_cliente", opts.id_cliente);
  if (opts.rp) qs.set("rp", String(opts.rp));
  if (opts.page) qs.set("page", String(opts.page));
  return apiFetch<{
    ok: true;
    fonte: string;
    modo: "login" | "nome";
    clientes_encontrados: number | null;
    /** Busca por nome: veio o máximo de clientes (6) e podem existir mais. */
    clientes_truncado?: boolean;
    /** Busca por nome: algum cliente tem mais logins do que os 50 trazidos. */
    logins_truncados?: boolean;
    total_upstream: number | null;
    page: number;
    rp: number;
    count: number;
    assinantes: RouterAssinante[];
  }>(`/api/admin/infra/router/assinantes?${qs.toString()}`, { ehAdmin: true });
}

// ─── DNS Manager ────────────────────────────────────────────────────────
export interface DnsPropagacao {
  dnsauth01: string | null;
  dnsauth02: string | null;
}
export interface DnsPtrResp {
  ok: true;
  ip: string;
  ptr: string | null;
  ttl: number | null;
  zone: string | null;
  name: string | null;
  found: boolean;
}
export type DnssecStatus =
  | "validado"
  | "assinado_nao_validado"
  | "sem_ds"
  | "falha_assinatura"
  | "falha_resolucao";
export interface DnssecAutoritativo {
  status: string | null;
  aa: boolean;
  ad: boolean;
  ptr: string | null;
  rrsig: boolean;
}
export interface DnsDnssec {
  ok: boolean;
  status: DnssecStatus | null;
  ad_flag: boolean;
  resolver?: string;
  reverse_name?: string;
  zone?: string | null;
  public_status?: string | null;
  autoritativos?: Record<string, DnssecAutoritativo | null>;
  ds?: { present: boolean; algorithm: number | null; digest_type: number | null } | null;
  detalhe: string;
}
export interface DnsUpsertResp {
  ok: true;
  message: string;
  ip: string;
  before: string | null;
  after: string;
  ttl: number;
  http_status: number;
  propagacao: DnsPropagacao | null;
  propagacao_consistente: boolean | null;
  propagacao_detalhe: string | null;
  dnssec: DnsDnssec | null;
}
export interface DnsPropResp {
  ok: boolean;
  ip: string;
  resultado: DnsPropagacao;
  consistente: boolean;
  detalhe: string;
}
export interface DnsAuditoriaItem {
  id: string;
  criado_em: string;
  operador_email: string;
  acao: string;
  ip: string | null;
  hostname: string | null;
  before: string | null;
  after: string | null;
  ttl: number | null;
  http_status: number | null;
  sucesso: number;
  erro: string | null;
  propagacao_json: string | null;
  dnssec_json: string | null;
  ip_origem: string | null;
}

export const dnsHealth = () =>
  apiFetch<{
    ok: boolean;
    configurado?: boolean;
    http_status?: number | null;
    zones?: number | null;
    ipv6_default_prefixlen?: number | null;
    ipv6_zones?: Array<{ prefix: string; zone: string }>;
  }>("/api/admin/dns/health", { ehAdmin: true });

export const dnsGetPtr = (ip: string) =>
  apiFetch<DnsPtrResp>(`/api/admin/dns/ptr/${encodeURIComponent(ip)}`, { ehAdmin: true });

export const dnsUpsertPtr = (p: {
  ip: string;
  ptr: string;
  ttl?: number;
  before?: string;
  /** Só IPv6: força o tamanho da zona ip6.arpa (nibble-aligned). */
  zone_prefixlen?: number;
}) =>
  apiFetch<DnsUpsertResp>("/api/admin/dns/ptr", {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify(p),
  });

export const dnsDeletePtr = (ip: string, before?: string) =>
  apiFetch<{ ok: true; message: string; ip: string; ja_ausente: boolean; removidos: number; zone: string | null }>(
    `/api/admin/dns/ptr/${encodeURIComponent(ip)}${before ? `?before=${encodeURIComponent(before)}` : ""}`,
    { ehAdmin: true, method: "DELETE" },
  );

export const dnsValidarPropagacao = (ip: string, esperado?: string) =>
  apiFetch<DnsPropResp>("/api/admin/dns/propagacao", {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify({ ip, esperado }),
  });

export const dnsValidarDnssec = (ip: string, zone?: string) =>
  apiFetch<{ ok: boolean; ip: string; dnssec: DnsDnssec; detalhe: string }>(
    "/api/admin/dns/dnssec",
    { ehAdmin: true, method: "POST", body: JSON.stringify(zone ? { ip, zone } : { ip }) },
  );

export interface RespostaDnsAuditoria {
  ok: true;
  registros: DnsAuditoriaItem[];
  /** Total do MESMO filtro (antes: só as 200 mais recentes, sem total). */
  total: number;
  pagina: number;
  limit: number;
  total_paginas: number;
  /** Sucesso/falha com os mesmos filtros da lista, menos o de status (KPI = lista). */
  contagens: { ok: number; falha: number };
  /** Ações que existem no banco inteiro (opções do filtro). */
  acoes: string[];
}
export const dnsAuditoria = (
  p: {
    limit?: number;
    ip?: string;
    q?: string;
    acao?: string;
    status?: "ok" | "falha";
    de?: string;
    ate?: string;
    ordenar?: "criado_em" | "acao" | "operador_email" | "ip";
    dir?: "asc" | "desc";
    pagina?: number;
  } = {},
) => {
  const qs = new URLSearchParams();
  if (p.limit) qs.set("limit", String(p.limit));
  if (p.ip) qs.set("ip", p.ip);
  if (p.q) qs.set("q", p.q);
  if (p.acao) qs.set("acao", p.acao);
  if (p.status) qs.set("status", p.status);
  if (p.de) qs.set("de", p.de);
  if (p.ate) qs.set("ate", p.ate);
  if (p.ordenar) qs.set("ordenar", p.ordenar);
  if (p.dir) qs.set("dir", p.dir);
  if (p.pagina && p.pagina > 1) qs.set("pagina", String(p.pagina));
  return apiFetch<RespostaDnsAuditoria>(`/api/admin/dns/auditoria${qs.toString() ? `?${qs.toString()}` : ""}`, { ehAdmin: true });
};

export interface DnsRegistroGenerico {
  type: string;
  name: string;
  content: string;
  ttl: number | null;
}
/** Tipos de registro aceitos pela validação da API real. */
export const DNS_TIPOS_REGISTRO = ["PTR", "A", "AAAA", "TXT", "CNAME", "NS"] as const;
export type DnsTipoRegistro = (typeof DNS_TIPOS_REGISTRO)[number];

export const dnsListZones = () =>
  apiFetch<{ ok: true; total: number; zones: string[] }>("/api/admin/dns/zones", { ehAdmin: true });

export interface DnsOverviewResp {
  ok: true;
  cache: boolean;
  totalZonas: number;
  totalRegistros: number;
  totalPtr: number;
  porTipo: Record<string, number>;
  zonasVazias: number;
  zonasVaziasList: string[];
  topZonas: Array<{ zone: string; total: number; ptr: number }>;
  zonasComErro: string[];
  geradoEm: string;
}
/** Inventário agregado de todas as zonas (cacheado ~10min no backend). */
export const dnsOverview = (refresh = false) =>
  apiFetch<DnsOverviewResp>(`/api/admin/dns/overview${refresh ? "?refresh=1" : ""}`, { ehAdmin: true });

/** Registros de uma zona (PTR/A/AAAA/NS/SOA/…). */
export const dnsZoneRecords = (zone: string) =>
  apiFetch<{ ok: true; zone: string; total: number; records: DnsRegistroGenerico[] }>(
    `/api/admin/dns/zones/${encodeURIComponent(zone)}/records`,
    { ehAdmin: true },
  );

/** Zonefile bruto da zona (somente leitura — nunca editado direto). */
export const dnsZonefile = (zone: string) =>
  apiFetch<{ ok: true; zone: string; text: string }>(
    `/api/admin/dns/zones/${encodeURIComponent(zone)}`,
    { ehAdmin: true },
  );

export const dnsCreateZone = (zone: string) =>
  apiFetch<{ ok: true; message: string; zone: string; http_status: number }>(
    "/api/admin/dns/zones",
    { ehAdmin: true, method: "POST", body: JSON.stringify({ zone }) },
  );

export const dnsDeleteZone = (zone: string) =>
  apiFetch<{ ok: true; message: string; zone: string; http_status: number }>(
    `/api/admin/dns/zones/${encodeURIComponent(zone)}`,
    { ehAdmin: true, method: "DELETE" },
  );

/** Cria zona(s) reversa(s) por prefixo CIDR (IPv4 /N → /24s; IPv6 nibble-aligned). */
export const dnsCreateReverseZones = (prefix: string) =>
  apiFetch<{
    ok: true;
    message: string;
    prefix: string;
    created: string[];
    existing: string[];
    zones: string[];
    http_status: number;
  }>("/api/admin/dns/reverse-zones", {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify({ prefix }),
  });

/** Adiciona um registro dentro de uma zona. */
export const dnsAddRecord = (
  zone: string,
  p: { name: string; type: DnsTipoRegistro; content: string; ttl?: number },
) =>
  apiFetch<{
    ok: true;
    message: string;
    zone: string;
    name: string;
    type: string;
    content: string;
    ttl: number | null;
    http_status: number;
  }>(`/api/admin/dns/zones/${encodeURIComponent(zone)}/records`, {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify(p),
  });

/** Remove registro(s) por name+type (+content opcional pra desambiguar). */
export const dnsDeleteRecordInZone = (
  zone: string,
  p: { name: string; type: DnsTipoRegistro; content?: string },
) =>
  apiFetch<{
    ok: true;
    message: string;
    zone: string;
    name: string;
    type: string;
    removidos: number;
    http_status: number;
  }>(`/api/admin/dns/zones/${encodeURIComponent(zone)}/records/delete`, {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify(p),
  });

/** Recarrega o BIND (rndc reload) no primary. */
export const dnsReload = () =>
  apiFetch<{ ok: true; message: string; http_status: number }>("/api/admin/dns/reload", {
    ehAdmin: true,
    method: "POST",
  });

// ===== Geofeed RFC 8805 =====

export interface GeofeedHealth {
  ok: true;
  phpipam_configurado: boolean;
  total_entradas: number;
  habilitadas: number;
  com_coordenada: number;
}

export type FonteGeofeed = "manual" | "seed_salvador" | "phpipam";

export interface GeofeedEntradaLocal {
  id: string;
  latitude: number | null;
  longitude: number | null;
  country_code: string;
  region_code: string | null;
  city: string | null;
  postal_code: string | null;
  habilitado: boolean;
  fonte: FonteGeofeed;
  atualizado_em: string;
}

export interface GeofeedSubnet {
  phpipam_id: string;
  cidr: string;
  mask: number;
  descricao: string | null;
  customer_id: string | number | null;
  publica: boolean;
  familia: "ipv4" | "ipv6";
  tem_entrada_local: boolean;
  entrada_local: GeofeedEntradaLocal | null;
}

export interface GeofeedListaResp {
  ok: true;
  total_phpipam: number;
  publicas: number;
  privadas: number;
  com_entrada_local: number;
  subnets: GeofeedSubnet[];
}

export interface GeofeedPatchResp {
  ok: true;
  entrada: {
    id: string;
    cidr: string;
    phpipam_subnet_id: string | null;
    latitude: number | null;
    longitude: number | null;
    country_code: string;
    region_code: string | null;
    city: string | null;
    postal_code: string | null;
    habilitado: number;
    fonte: FonteGeofeed;
    atualizado_em: string;
    atualizado_por: string | null;
  };
}

export const geofeedHealth = () =>
  apiFetch<GeofeedHealth>("/api/admin/geofeed/health", { ehAdmin: true });

export const geofeedListarSubnets = (
  filtro: "publicas" | "privadas" | "todas" = "publicas",
  familia?: "ipv4" | "ipv6",
) => {
  const qs = new URLSearchParams({ filtro });
  if (familia) qs.set("familia", familia);
  return apiFetch<GeofeedListaResp>(`/api/admin/geofeed/subnets?${qs.toString()}`, {
    ehAdmin: true,
  });
};

export const geofeedAtualizar = (
  id: string,
  patch: {
    latitude?: number | null;
    longitude?: number | null;
    country_code?: string;
    region_code?: string | null;
    city?: string | null;
    postal_code?: string | null;
    habilitado?: boolean;
  },
) =>
  apiFetch<GeofeedPatchResp>(`/api/admin/geofeed/${encodeURIComponent(id)}`, {
    ehAdmin: true,
    method: "PATCH",
    body: JSON.stringify(patch),
  });


export interface GeofeedLookupIxcResp {
  ok: true;
  cliente_local: { id: string; nome: string };
  ixc: {
    id: string;
    razao: string;
    fantasia: string | null;
    endereco: string | null;
    numero: string | null;
    bairro: string | null;
    cidade: string | null;
    cep: string | null;
    uf: string | null;
  };
  sugestao_geofeed: {
    city: string | null;
    region_code: string | null;
    postal_code: string | null;
    country_code: string;
    endereco_busca: string;
  };
}

/** Cruza phpIPAM customer → cliente local → IXC e devolve cadastro
 *  (CEP, cidade, UF, endereço) pra auto-preencher o drawer Geofeed. */
export const geofeedLookupIxc = (phpipamCustomerId: string) =>
  apiFetch<GeofeedLookupIxcResp>(
    `/api/admin/geofeed/lookup-ixc?phpipam_customer_id=${encodeURIComponent(phpipamCustomerId)}`,
    { ehAdmin: true },
  );

export interface GeofeedEntradaCompleta {
  id: string;
  phpipam_subnet_id: string | null;
  cidr: string;
  latitude: number | null;
  longitude: number | null;
  country_code: string;
  region_code: string | null;
  city: string | null;
  postal_code: string | null;
  habilitado: number;
  fonte: FonteGeofeed;
  criado_em: string;
  atualizado_em: string;
  atualizado_por: string | null;
}

export const geofeedListarEntradas = (somenteStandalone = false) => {
  const qs = somenteStandalone ? "?somente_standalone=1" : "";
  return apiFetch<{ ok: true; total: number; entradas: GeofeedEntradaCompleta[] }>(
    `/api/admin/geofeed/entradas${qs}`,
    { ehAdmin: true },
  );
};

export const geofeedCriar = (dados: {
  cidr: string;
  latitude: number;
  longitude: number;
  country_code?: string;
  region_code?: string | null;
  city?: string | null;
  postal_code?: string | null;
  habilitado?: boolean;
}) =>
  apiFetch<{ ok: true; entrada: GeofeedEntradaCompleta }>("/api/admin/geofeed", {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify(dados),
  });

export const geofeedDeletar = (id: string) =>
  apiFetch<{ ok: true; removida: { id: string; cidr: string } }>(
    `/api/admin/geofeed/${encodeURIComponent(id)}`,
    { ehAdmin: true, method: "DELETE" },
  );

export interface GeofeedPublicacao {
  id: string;
  gerado_em: string;
  gerado_por_id: string | null;
  gerado_por_email: string;
  total_prefixos: number;
  total_ipv4: number;
  total_ipv6: number;
  sha256: string;
  r2_key: string;
  observacao: string | null;
}

export const geofeedPublicar = (opts: { observacao?: string; pular_se_igual?: boolean } = {}) =>
  apiFetch<
    | { ok: true; publicada: true; publicacao: GeofeedPublicacao }
    | { ok: true; publicada: false; motivo: "conteudo_identico"; ultima: GeofeedPublicacao }
  >("/api/admin/geofeed/publicar", {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify(opts),
  });

export interface RespostaGeofeedPublicacoes {
  ok: true;
  /** Total REAL (COUNT do mesmo filtro). Antes era o tamanho da página. */
  total: number;
  pagina: number;
  limit: number;
  total_paginas: number;
  publicacoes: GeofeedPublicacao[];
}
export const geofeedListarPublicacoes = (limit = 50, pagina = 1, q?: string) => {
  const qs = new URLSearchParams({ limit: String(limit), pagina: String(pagina) });
  if (q) qs.set("q", q);
  return apiFetch<RespostaGeofeedPublicacoes>(`/api/admin/geofeed/publicacoes?${qs}`, { ehAdmin: true });
};

/** URL absoluta pra disparar download via <a href> (browser pega cookies CF). */
export const geofeedUrlDownloadPublicacao = (id: string) =>
  `${BASE}/api/admin/geofeed/publicacoes/${encodeURIComponent(id)}/download`;

// ─── Módulo de Circuitos (onda 1) ──────────────────────────────────────
export interface ServicoCircuito {
  id: string;
  code: string;
  circuit_id?: string | null;
  /** Identificador de antes da última troca de produto (migration 026). */
  circuit_id_anterior?: string | null;
  circuit_id_trocado_em?: string | null;
  /** Ponta A — referência do circuito, sempre exibida (inclusive em transporte). */
  ponta_a?: {
    endpoint: string;
    device_id: string;
    parent_interface: string;
    outer_vlan: number | null;
    localidade_sigla: string | null;
  } | null;
  total_pontas?: number;
  /**
   * TODAS as entregas do circuito. Dupla abordagem tem duas, e a listagem
   * mostra as duas — antes vinha só a ponta A e o circuito parecia ter uma.
   */
  entregas?: Array<{
    endpoint: string;
    role: string;
    device_id: string;
    parent_interface: string | null;
    outer_vlan: number | null;
    localidade_sigla: string | null;
  }>;
  /**
   * Último bloqueio/desbloqueio CONCLUÍDO. Vem de automation_jobs — não há
   * tabela de histórico separada, e é de propósito: duas fontes para o mesmo
   * fato acabam discordando.
   *
   * ⚠️ `null` num circuito BLOQUEADO não é campo faltando: quer dizer que o
   * bloqueio não passou pelo Sentinela. A tela precisa DIZER isso.
   */
  ultima_mudanca?: {
    tipo: "BLOQUEAR" | "DESBLOQUEAR";
    em: string;
    por: string | null;
    aprovado_por: string | null;
  } | null;
  cliente_id: string;
  cliente_nome: string | null;
  ixc_contrato_id: string | null;
  /** Número do contrato no IXC — é por ele que o financeiro identifica o cliente. */
  ixc_contrato_numero: string | null;
  product_type: string;
  lifecycle_state: string;
  desired_admin_state: string;
  observed_admin_state: string | null;
  routing_mode: string;
  observacoes: string | null;
  created_at: string;
}

export interface CircuitoAnexo {
  id: string;
  code: string;
  outer_vlan: number | null;
  mtu: number | null;
  device_id: string;
  parent_interface: string;
  state: string;
}

export function listarServicosCircuito(
  opts: {
    busca?: string;
    estado?: string;
    pagina?: number;
    limit?: number;
    /** Coluna: situacao (default) | cliente | contrato | servico | onde | criado */
    ordenarPor?: string;
    direcao?: "asc" | "desc";
  } = {},
) {
  const p: string[] = [];
  if (opts.busca) p.push(`busca=${encodeURIComponent(opts.busca)}`);
  if (opts.estado) p.push(`estado=${encodeURIComponent(opts.estado)}`);
  if (opts.pagina && opts.pagina > 1) p.push(`pagina=${opts.pagina}`);
  if (opts.limit) p.push(`limit=${opts.limit}`);
  // A ordenação é do SERVIDOR: com paginação, ordenar a página ordena 30 de 74.
  if (opts.ordenarPor) p.push(`ordenar_por=${encodeURIComponent(opts.ordenarPor)}`);
  if (opts.direcao) p.push(`direcao=${opts.direcao}`);
  return apiFetch<{
    ok: true;
    servicos: ServicoCircuito[];
    total: number;
    pagina: number;
    total_paginas: number;
  }>(`/api/admin/circuitos${p.length ? `?${p.join("&")}` : ""}`, { ehAdmin: true });
}

export function obterServicoCircuito(id: string) {
  return apiFetch<{
    ok: true;
    servico: ServicoCircuito & {
      tipo_cliente: string | null;
      ixc_contrato_numero: string | null;
      circuit_id: string | null;
      vrf_ref: string | null;
      redundancy_mode: string;
      /** Controle otimista de concorrência — enviado de volta no PATCH. */
      version: number;
      cliente_nome: string | null;
    };
    circuitos: Array<
      CircuitoAnexo & {
        endpoint: string;
        localidade_sigla: string | null;
        vlanif: string | null;
        /** SECONDARY = segunda abordagem do mesmo serviço (dupla abordagem). */
        role?: "PRIMARY" | "SECONDARY" | "OTHER";
      }
    >;
    qos: Array<{ upstream_bps: number; downstream_bps: number }>;
    /**
     * Bloqueios e desbloqueios do circuito, do mais recente para o mais antigo.
     * Inclui o que FALHOU e o que sofreu rollback — é justamente isso que
     * alguém procura ao investigar "por que o cliente ficou sem acesso".
     */
    historico?: Array<{
      id: string;
      tipo: "BLOQUEAR" | "DESBLOQUEAR";
      status: string;
      solicitante_email: string | null;
      aprovador_email: string | null;
      aprovado_em: string | null;
      concluido_em: string | null;
      created_at: string;
      erro: string | null;
    }>;
    nome_exibicao: string | null;
  }>(`/api/admin/circuitos/${id}`, { ehAdmin: true });
}

/**
 * Exclui o circuito do INVENTÁRIO. Não toca o equipamento: se a Vlanif estiver
 * mesmo no switch, ela volta como "não cadastrado" na próxima reconciliação.
 * Recusa (409) quando há job em andamento no circuito.
 */
export function excluirCircuito(id: string) {
  return apiFetch<{ ok: true; excluido: { id: string; circuit_id: string | null; code: string } }>(
    `/api/admin/circuitos/${id}`,
    { method: "DELETE", ehAdmin: true },
  );
}

/**
 * ⚠️ `sigla` é a que o produto empresta ao circuit ID (LD-C-23142-…). A fonte
 * de verdade é `product_rules` no banco; aqui ela existe só para a tela poder
 * avisar, ANTES de salvar, que trocar o produto regera o identificador.
 */
export const PRODUTOS_CIRCUITO = [
  { valor: "internet", rotulo: "Internet", pontas: 1, sigla: "LD" },
  { valor: "transporte_dados", rotulo: "Transporte de dados", pontas: 2, sigla: "TD" },
  { valor: "transito_ip", rotulo: "Trânsito IP (provedores)", pontas: 1, sigla: "TI" },
  { valor: "l2vpn", rotulo: "L2VPN", pontas: 2, sigla: "L2" },
  { valor: "l3vpn", rotulo: "L3VPN", pontas: 2, sigla: "L3" },
  { valor: "clear_channel_qinq", rotulo: "Transporte clear channel (QinQ)", pontas: 2, sigla: "CC" },
] as const;

export type ProdutoCircuito = (typeof PRODUTOS_CIRCUITO)[number]["valor"];

/** Uma ponta do serviço. A VLAN define a Vlanif onde as ações são aplicadas. */
export interface PontaCircuito {
  endpoint: string;
  device_id: string;
  /** Porta física/Eth-Trunk — OPCIONAL: a VLAN pode chegar por trunk ou CPE. */
  parent_interface?: string;
  /** Interface L3 no equipamento (ex.: Vlanif725). Derivada da VLAN se omitida. */
  vlanif?: string;
  outer_vlan?: number;
  mtu?: number;
  /**
   * PRIMARY = entrega principal. SECONDARY = segunda abordagem do MESMO
   * serviço (dupla abordagem): não entra na rota do identificador e o bloqueio
   * age nela também.
   */
  role?: "PRIMARY" | "SECONDARY" | "OTHER";
  /** Sigla CNL da localidade desta ponta (ex.: SDR, FSA) — compõe a rota do ID. */
  localidade_sigla?: string;
}

export function criarServicoCircuito(dados: {
  /** Cliente local, quando já existe no Sentinela. */
  cliente_id?: string;
  /** Cliente do IXC — criado localmente no cadastro se ainda não existir. */
  ixc_cliente_id_novo?: string;
  cliente_nome?: string;
  cliente_documento?: string;
  ixc_contrato_id: string;
  ixc_cliente_id?: string;
  tipo_cliente: "C" | "I";
  product_type: ProdutoCircuito;
  pontas: PontaCircuito[];
  upstream_mbps?: number;
  downstream_mbps?: number;
  observacoes?: string;
}) {
  return apiFetch<{
    ok: true;
    servico: ServicoCircuito & { circuit_id: string | null };
    circuitos: CircuitoAnexo[];
    nome_exibicao: string | null;
    ixc: unknown;
  }>(
    "/api/admin/circuitos",
    { ehAdmin: true, method: "POST", body: JSON.stringify(dados) },
  );
}

/** Checa conflito de VLAN ANTES de salvar (o banco também barra, via índice parcial). */
export function checarVlanDisponivel(device_id: string, vlan: number) {
  const q = `device_id=${encodeURIComponent(device_id)}&vlan=${vlan}`;
  return apiFetch<{
    ok: true;
    disponivel: boolean;
    em_uso_por: string | null;
    planejados: string[];
  }>(`/api/admin/circuitos/vlans/disponivel?${q}`, { ehAdmin: true });
}

/** Contratos do cliente no IXC — compõem o circuit ID. */
export function listarContratosIxc(ixc_cliente_id: string) {
  return apiFetch<{
    ok: true;
    ocultos_inativos?: number;
    contratos: Array<{
      id: string;
      descricao: string;
      status: string;
      status_internet: string;
      data_ativacao: string | null;
      ativo: boolean;
      obs_atual: string | null;
    }>;
  }>(`/api/admin/circuitos/ixc/contratos?ixc_cliente_id=${encodeURIComponent(ixc_cliente_id)}`, {
    ehAdmin: true,
  });
}

/** Busca nas siglas oficiais de localidade (CNL). */
export function buscarLocalidadesCnl(busca: string) {
  return apiFetch<{
    ok: true;
    localidades: Array<{ sigla: string; uf: string; localidade: string; municipio: string }>;
    /** true = lista inicial (UF de operação), sem termo de busca. */
    padrao: boolean;
    uf: string;
  }>(`/api/admin/circuitos/localidades?busca=${encodeURIComponent(busca)}`, { ehAdmin: true });
}

/** Edita serviço e pontas. `version` faz controle otimista de concorrência. */
export function editarServicoCircuito(
  id: string,
  dados: {
    version?: number;
    observacoes?: string | null;
    upstream_mbps?: number | null;
    downstream_mbps?: number | null;
    /** Troca de produto: regera o identificador quando a sigla muda. */
    product_type?: ProdutoCircuito;
    pontas?: Array<{
      /** Sem `id` a ponta é NOVA; com `remover` ela sai do circuito. */
      id?: string;
      remover?: boolean;
      endpoint?: string;
      device_id?: string;
      parent_interface?: string | null;
      vlanif?: string | null;
      outer_vlan?: number | null;
      localidade_sigla?: string | null;
      role?: "PRIMARY" | "SECONDARY" | "OTHER";
    }>;
  },
) {
  return apiFetch<{
    ok: true;
    circuit_id?: string;
    circuit_id_anterior?: string;
    product_type?: string;
  }>(`/api/admin/circuitos/${id}`, {
    ehAdmin: true,
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

// ─── Reconciliação (onda 2) ────────────────────────────────────────────
export interface DivergenciaCircuito {
  id: string;
  tipo:
    | "AUSENTE_NO_EQUIPAMENTO"
    | "NAO_CADASTRADO"
    | "ESTADO_DIVERGENTE"
    /** Bloqueado no cadastro, mas só parte das entregas em shutdown (migration 027). */
    | "BLOQUEIO_PARCIAL"
    | "BANDA_DIVERGENTE";
  /** `dupla_abordagem`, `entregas` e `entregas_no_estado` no bloqueio incompleto. */
  detalhe?: Record<string, unknown> | null;
  device_id: string;
  vlanif: string | null;
  esperado: string;
  observado: string;
  resolvida: boolean;
  detectada_em: string;
  resolvida_em: string | null;
  circuit_id: string | null;
  servico_code: string | null;
}

export function listarDivergencias(incluirResolvidas = false) {
  return apiFetch<{ ok: true; divergencias: DivergenciaCircuito[] }>(
    `/api/admin/circuitos/divergencias${incluirResolvidas ? "?incluir_resolvidas=true" : ""}`,
    { ehAdmin: true },
  );
}

/**
 * Indicadores da tela de circuitos, agregados NO BANCO.
 *
 * Existe porque KPI calculado sobre a página que está na tela muda quando a
 * pessoa pagina — e o badge de divergências chegou a exibir 200, que era o
 * LIMIT da consulta e não o total.
 */
/* ─── Estado operacional dos circuitos (LibreNMS) ───────────────────────────
 *
 * Alimentado pelo cron de 5 min do worker (lib/estado-circuitos.ts). As
 * categorias vêm decididas do BANCO de propósito: o mesmo critério alimenta o
 * KPI e a lista, e duas implementações da mesma regra divergem no primeiro
 * ajuste. Ver o cabeçalho de routes/admin/circuitos-estado.ts.
 */
export type CategoriaEstado =
  | "caiu"
  | "caindo"
  | "bloqueado"
  | "nao_aplicado"
  | "sem_registro"
  | "cego"
  | "ok";

export interface CircuitoEstadoItem {
  service_id: string;
  code: string;
  circuit_id: string | null;
  ixc_contrato_numero: string | null;
  cliente_id: string | null;
  cliente_nome: string | null;
  produto: string | null;
  ponta_id: string;
  device_id: string;
  vlanif: string | null;
  outer_vlan: number | null;
  papel: string | null;
  equipamento: string | null;
  ip_gerencia: string | null;
  oper: string | null;
  admin: string | null;
  alias: string | null;
  caiu_em: string | null;
  desde_antes: boolean | null;
  /** Piso: sabemos que caiu ANTES disto (horizonte do eventlog do LibreNMS).
   *  Preenchido so quando a leitura do eventlog foi completa. */
  caiu_antes_de: string | null;
  visto_em: string | null;
  /** Quando esta interface passou a ser observada. Limite inferior de
   *  "fora ha quanto tempo" para quem ja estava caido antes disso. */
  observando_desde: string | null;
  intencao: string | null;
  categoria: CategoriaEstado;
  minutos_fora: number | null;
}

export interface ResumoEstadoCircuitos {
  ok: true;
  estado: {
    pontas: number;
    caiu: number;
    caindo: number;
    bloqueado: number;
    nao_aplicado: number;
    sem_registro: number;
    cego: number;
    ok: number;
    circuitos_caidos: number;
  };
  leitura: {
    equipamentos: number;
    interfaces: number;
    mais_recente: string | null;
    mais_antiga: string | null;
  };
  movimento: { quedas_24h: number; subidas_24h: number };
  corte_minutos: number;
  gerado_em: string;
}

export interface ListaEstadoCircuitos {
  ok: true;
  itens: CircuitoEstadoItem[];
  total: number;
  pagina: number;
  limit: number;
  corte_minutos: number;
}

export async function obterResumoEstadoCircuitos() {
  return apiFetch<ResumoEstadoCircuitos>("/api/admin/circuitos/estado/resumo", { ehAdmin: true });
}

export async function listarEstadoCircuitos(params: {
  categoria?: string;
  busca?: string;
  pagina?: number;
  limit?: number;
}) {
  const q = new URLSearchParams();
  if (params.categoria) q.set("categoria", params.categoria);
  if (params.busca) q.set("busca", params.busca);
  if (params.pagina) q.set("pagina", String(params.pagina));
  if (params.limit) q.set("limit", String(params.limit));
  return apiFetch<ListaEstadoCircuitos>(`/api/admin/circuitos/estado?${q}`, { ehAdmin: true });
}

/**
 * Prévia da gerência pró-ativa: o que SAIRIA se a trava estivesse ligada.
 *
 * ⚠️ Não colocar em refetch automático. Cada chamada consulta o IXC (chamado
 * aberto, contrato ativo) e o SAC (contatos) por cliente — é cara e não muda a
 * cada minuto. Carrega sob demanda, quando alguém abre o painel.
 */
export interface PreviaAvisoCircuito {
  tipo: "QUEDA" | "RETORNO" | "OITO_HORAS" | "DIARIO" | "ENCERRAMENTO" | "SUPRIMIDO";
  motivo?: string;
  cliente: string | null;
  ixc_cliente: string | null;
  circuitos: string[];
  contrato: string | null;
  fora_ha: string | null;
  chamado: string | null;
  para: string[];
  cc: string[];
  assunto: string | null;
  corpo: string | null;
}

export interface PreviaAvisos {
  trava: boolean;
  candidatos: number;
  sairiam: number;
  suprimidos: number;
  por_tipo: Record<string, number>;
  itens: PreviaAvisoCircuito[];
  ignorado?: string;
}

export async function previaAvisosCircuitos() {
  return apiFetch<PreviaAvisos>("/api/admin/circuitos/estado/avisos/previa", { ehAdmin: true });
}

export interface EquipamentoDisponivel {
  device_id: string;
  nome: string | null;
  ip_gerencia: string | null;
  plataforma: string | null;
  pontas: number;
}

/**
 * Equipamentos que podem receber uma ponta de circuito. Alimenta a SELEÇÃO do
 * campo de equipamento — que era texto livre e exigia adivinhar o device_id.
 */
export async function listarEquipamentosDisponiveis() {
  return apiFetch<{ ok: true; itens: EquipamentoDisponivel[] }>(
    "/api/admin/circuitos/equipamentos-disponiveis",
    { ehAdmin: true },
  );
}

// ─── Manutenção programada ────────────────────────────────────────────────
export type TipoManutencao = "PROGRAMADA" | "EMERGENCIAL";
export type ImpactoManutencao = "INTERRUPCAO_TOTAL" | "INTERMITENTE" | "SEM_IMPACTO_PREVISTO";
export type StatusManutencao = "RASCUNHO" | "PUBLICADA" | "EM_ANDAMENTO" | "CONCLUIDA" | "CANCELADA";

export interface ManutencaoItem {
  id: string;
  codigo: string;
  tipo: TipoManutencao;
  titulo: string;
  motivo: string;
  impacto: ImpactoManutencao;
  inicio: string;
  fim: string;
  status: StatusManutencao;
  chamado_ixc: string | null;
  responsavel_email: string | null;
  observacoes_internas: string | null;
  motivo_cancelamento: string | null;
  publicada_em: string | null;
  concluida_em: string | null;
  alvos: number;
  extensoes: number;
  clientes_avisados: number;
  /** Janelas abertas que se sobrepõem no tempo E compartilham circuitos. */
  conflitos: number;
}

export interface ConflitoJanela {
  id: string;
  codigo: string;
  titulo: string;
  status: string;
  inicio: string;
  fim: string;
  circuitos_em_comum: number;
}

export interface EquipamentoAlvo {
  device_id: string;
  nome: string | null;
  circuitos: number;
  clientes: number;
}

export interface AlcanceManutencao {
  ok: true;
  circuitos: Array<{
    service_id: string;
    code: string;
    circuit_id: string | null;
    contrato: string | null;
    ixc_cliente_id: string | null;
    produto: string | null;
    localidade: string | null;
    device_id: string | null;
    via: "EQUIPAMENTO" | "CIRCUITO";
  }>;
  total_circuitos: number;
  total_clientes: number;
  sem_cliente: number;
}

const MANUT = "/api/admin/circuitos/manutencoes";

export async function listarManutencoes() {
  return apiFetch<{
    ok: true;
    itens: ManutencaoItem[];
    prazo_contratual_h: number;
    tolerancia_pos_fim_min: number;
  }>(MANUT, { ehAdmin: true });
}

export async function listarEquipamentosAlvo() {
  return apiFetch<{ ok: true; itens: EquipamentoAlvo[] }>(`${MANUT}/equipamentos`, { ehAdmin: true });
}

export interface CircuitoAlvo {
  service_id: string;
  code: string;
  circuit_id: string | null;
  contrato: string | null;
  ixc_cliente_id: string | null;
  produto: string | null;
  device_id: string | null;
  vlanif: string | null;
  localidade: string | null;
  /** ifAlias do LibreNMS — é onde vive o nome do cliente. */
  alias: string | null;
}

/**
 * Sem `busca`, devolve todos os circuitos ativos — a lista inteira, para o
 * seletor abrir e o operador escolher sem precisar adivinhar o termo.
 * O filtro fica no navegador; a lista é carregada uma vez e cacheada.
 */
export async function buscarCircuitosAlvo(busca?: string) {
  const q = busca ? `?${new URLSearchParams({ busca })}` : "";
  return apiFetch<{ ok: true; itens: CircuitoAlvo[] }>(`${MANUT}/circuitos${q}`, { ehAdmin: true });
}

export async function conflitosManutencao(id: string) {
  return apiFetch<{ ok: true; conflitos: ConflitoJanela[]; com_circuitos_em_comum: number }>(
    `${MANUT}/${id}/conflitos`,
    { ehAdmin: true },
  );
}

export async function alcanceManutencao(id: string) {
  return apiFetch<AlcanceManutencao>(`${MANUT}/${id}/alcance`, { ehAdmin: true });
}

export async function criarManutencao(corpo: {
  tipo: TipoManutencao;
  titulo: string;
  motivo: string;
  impacto: ImpactoManutencao;
  inicio: string;
  fim: string;
  chamado_ixc?: string | null;
  observacoes_internas?: string | null;
  alvos: Array<{ tipo: "EQUIPAMENTO" | "CIRCUITO"; device_id?: string | null; service_id?: string | null }>;
}) {
  return apiFetch<{ ok: true; manutencao: ManutencaoItem }>(MANUT, {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify(corpo),
  });
}

export interface AlvoManutencao {
  tipo: "EQUIPAMENTO" | "CIRCUITO";
  device_id: string | null;
  service_id: string | null;
  code: string | null;
  circuit_id: string | null;
}

export async function obterManutencao(id: string) {
  return apiFetch<{
    ok: true;
    manutencao: ManutencaoItem;
    alvos: AlvoManutencao[];
    extensoes: unknown[];
    avisos: unknown[];
  }>(`${MANUT}/${id}`, { ehAdmin: true });
}

/**
 * Só rascunho. Publicada muda por estender/cancelar, que avisam o cliente.
 *
 * Aceita PARCIAL porque a rota é um PATCH de verdade: campo ausente não é
 * tocado, campo presente com null limpa. É o que permite "só mudar o tipo para
 * emergencial" sem reenviar o formulário inteiro.
 */
export async function editarManutencao(
  id: string,
  corpo: Partial<Parameters<typeof criarManutencao>[0]>,
) {
  return apiFetch<{ ok: true }>(`${MANUT}/${id}`, {
    ehAdmin: true,
    method: "PATCH",
    body: JSON.stringify(corpo),
  });
}

/**
 * Pode falhar por prazo contratual — o erro traz as horas que faltam.
 *
 * `{ tipo: "EMERGENCIAL" }` publica a janela como emergencial NUMA chamada só: o
 * servidor confere todas as pré-condições (prazo, alvos, conflito) com esse tipo e
 * só então grava tipo + status juntos. Não substituir por `editarManutencao` +
 * `publicarManutencao`: se a publicação fosse recusada, o rascunho ficava gravado
 * como EMERGENCIAL (pago em 11/10/2026).
 */
export async function publicarManutencao(id: string, opcoes?: { tipo: "EMERGENCIAL" }) {
  return apiFetch<{ ok: true }>(`${MANUT}/${id}/publicar`, {
    ehAdmin: true,
    method: "POST",
    ...(opcoes ? { body: JSON.stringify(opcoes) } : {}),
  });
}

export async function estenderManutencao(id: string, fim: string, motivo: string) {
  return apiFetch<{ ok: true; ordem: number }>(`${MANUT}/${id}/estender`, {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify({ fim, motivo }),
  });
}

export async function concluirManutencao(id: string) {
  return apiFetch<{ ok: true }>(`${MANUT}/${id}/concluir`, { ehAdmin: true, method: "POST" });
}

export async function cancelarManutencao(id: string, motivo: string) {
  return apiFetch<{ ok: true }>(`${MANUT}/${id}/cancelar`, {
    ehAdmin: true,
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

export async function excluirManutencao(id: string) {
  return apiFetch<{ ok: true }>(`${MANUT}/${id}`, { ehAdmin: true, method: "DELETE" });
}

export interface EventoPorta {
  de: string | null;
  para: string;
  em: string;
}

export async function historicoPorta(deviceId: string, vlanif: string) {
  const q = new URLSearchParams({ device_id: deviceId, vlanif });
  return apiFetch<{ ok: true; eventos: EventoPorta[] }>(
    `/api/admin/circuitos/estado/historico?${q}`,
    { ehAdmin: true },
  );
}

export interface ResumoCircuitos {
  ok: true;
  circuitos: { total: number; liberados: number; bloqueados: number; desativados: number; sem_identificador: number };
  pedidos: { aguardando: number; em_andamento: number; concluidos_24h: number; falhas_24h: number };
  divergencias: { total: number; por_tipo: Record<string, number>; acionaveis: number };
  leitura: { switches: number; interfaces: number; mais_recente: string | null; mais_antiga: string | null };
  gerado_em: string;
}

export function resumoCircuitos() {
  return apiFetch<ResumoCircuitos>("/api/admin/circuitos/resumo", { ehAdmin: true });
}

/** Dispara a comparação. NUNCA corrige — só registra (P-02 da especificação). */
export function reconciliarCircuitos() {
  return apiFetch<{
    ok: true;
    comparados: number;
    observados: number;
    divergencias: number;
    novas: number;
    resolvidas: number;
    corrigido: number;
  }>("/api/admin/circuitos/reconciliar", { ehAdmin: true, method: "POST" });
}

/**
 * Busca cliente NO IXC (não na base local do Sentinela, que tem só os clientes
 * vindos de alertas). Já devolve os contratos ATIVOS e DISPONÍVEIS — cliente
 * cujo único contrato já tem circuito não vem na lista.
 *
 * ⚠️ A lista é RECORTE: `total_no_ixc` diz quantos casam com o termo. Termo
 * curto casa com centenas ("SILVA" dá 772), e sem mostrar esse número o
 * operador lê a lista parcial como "o cliente não existe".
 */
export function buscarClientesParaCircuito(termo: string) {
  return apiFetch<{
    ok: true;
    clientes: Array<{
      ixc_id: string;
      nome: string;
      documento: string | null;
      contratos: Array<{ id: string; descricao: string; data_ativacao: string | null }>;
      contratos_ativos: number;
      contratos_ja_usados: number;
      /** Preenchido só no atalho por contrato: por que ele não pode ser usado. */
      motivo_indisponivel?: string | null;
    }>;
    /** true quando o termo era um número de contrato e resolveu direto. */
    por_contrato?: boolean;
    /** Quantos clientes o IXC tem para o termo — pode ser MUITO maior que a lista. */
    total_no_ixc?: number;
    limite?: number;
  }>(`/api/admin/circuitos/clientes/buscar?termo=${encodeURIComponent(termo)}`, { ehAdmin: true });
}

// ─── Circuitos: fila de jobs (onda 3) ─────────────────────────────────

export interface JobCircuito {
  id: string;
  /** Circuito a que o pedido pertence. Chave real — `code` vem do jsonb. */
  service_id: string | null;
  tipo: "BLOQUEAR" | "DESBLOQUEAR" | "ALTERAR_BANDA";
  status: string;
  device_id: string;
  alvo: string | null;
  solicitante_email: string;
  aprovador_email: string | null;
  erro: string | null;
  simulado: boolean;
  created_at: string;
  concluido_em: string | null;
  motivo: string | null;
  circuit_id: string | null;
  code: string | null;
}

export function listarJobsCircuito(
  status?: string,
  ordem?: { por: string; dir: "asc" | "desc" },
) {
  const p: string[] = [];
  if (status) p.push(`status=${encodeURIComponent(status)}`);
  // Ordenação no servidor: o LIMIT 100 é aplicado DEPOIS dela, então é ela que
  // decide quais 100 chegam — por isso pendente no topo nunca cai fora.
  if (ordem) p.push(`ordenar_por=${encodeURIComponent(ordem.por)}`, `direcao=${ordem.dir}`);
  const q = p.length ? `?${p.join("&")}` : "";
  return apiFetch<{
    ok: true;
    jobs: JobCircuito[];
    tipos_liberados: Array<{ tipo: string; liberado_por: string; liberado_em: string }>;
    /** Tipos já ensaiados no canary e ainda não liberados, com a evidência. */
    tipos_elegiveis: Array<{
      tipo: string;
      job_evidencia: string;
      concluido_em: string;
      solicitante_email: string;
      aprovador_email: string;
    }>;
  }>(`/api/admin/circuitos/jobs${q}`, { ehAdmin: true });
}

export function obterJobCircuito(id: string) {
  return apiFetch<{
    ok: true;
    job: Record<string, unknown>;
    steps: Array<{ ordem: number; descricao: string; comando: string | null; saida: string | null; ok: boolean | null; duracao_ms: number | null }>;
  }>(`/api/admin/circuitos/jobs/${id}`, { ehAdmin: true });
}

export function criarJobCircuito(dados: {
  tipo: "BLOQUEAR" | "DESBLOQUEAR";
  service_id: string;
  motivo: string;
  idempotencyKey: string;
}) {
  return apiFetch<{
    ok: true;
    job_id: string;
    status: string;
    plano: { comandos: string[]; inverso: string[]; verify: Record<string, unknown> };
    snapshot_antes: Record<string, unknown>;
    simulacao_ativa: boolean;
    proximo_passo: string;
  }>(`/api/admin/circuitos/jobs`, {
    method: "POST",
    ehAdmin: true,
    headers: { "Idempotency-Key": dados.idempotencyKey },
    body: JSON.stringify({ tipo: dados.tipo, service_id: dados.service_id, motivo: dados.motivo }),
  });
}

export function liberarTipoCircuito(tipo: string, jobEvidencia: string) {
  return apiFetch<{ ok: true; tipo: string; liberado_por: string }>(
    `/api/admin/circuitos/jobs/tipos/${tipo}/liberar`,
    { method: "POST", ehAdmin: true, body: JSON.stringify({ job_evidencia: jobEvidencia }) },
  );
}

export function aprovarJobCircuito(id: string) {
  return apiFetch<{ ok: true; status: string; detalhes: string }>(
    `/api/admin/circuitos/jobs/${id}/aprovar`,
    { method: "POST", ehAdmin: true, body: "{}" },
  );
}

export function cancelarJobCircuito(id: string) {
  return apiFetch<{ ok: true; status: string }>(
    `/api/admin/circuitos/jobs/${id}/cancelar`,
    { method: "POST", ehAdmin: true, body: "{}" },
  );
}
